import type { FormEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CustomerManagerApiKeyDetail,
  CustomerManagerCustomer360FullResponse,
  CustomerManagerCustomer360Response,
  CustomerManagerLifecycleEvidence,
  CustomerManagerRequestLifecycleEvent,
  CustomerManagerRequestLifecycleResponse,
  CustomerManagerRequestLogRow,
  OrgSkuRateCore,
  OrgSkuRateView,
} from '@nadoutong/contracts';
import {
  ArrowLeft,
  ArrowLeftRight,
  Activity,
  Ban,
  ChevronUp,
  ChevronsDownUp,
  ChevronsUpDown,
  CircleAlert,
  ExternalLink,
  Eye,
  EyeOff,
  Filter,
  Gauge,
  Gift,
  HandCoins,
  KeyRound,
  Layers,
  LayoutDashboard,
  ReceiptText,
  RefreshCw,
  RotateCcw,
  Save,
  ShieldAlert,
  Split,
  Timer,
  Undo2,
  Waypoints,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { OpsApiError } from '../api/client.ts';
import {
  modelPlanManagedFromError,
  useCloneModelPlan,
  useOrgModelPlan,
  useUpdateOrgModelPlan,
} from '../api/modelPlans.ts';
import { ModelPlanSelect } from '../components/ModelPlanSelect.tsx';
import type { CustomerManagerRequestLogScope } from '../api/customerManagerWorkspace.ts';
import {
  useCustomerManagerCreditLots,
  useCustomerManagerCustomer360,
  useCustomerManagerRequestLifecycle,
  useCustomerManagerRequestLogs,
  useIssueCustomerManagerGift,
  useRedeemCustomerManagerGiftCard,
  useRevokeCustomerManagerApiKey,
  useUpdateCustomerManagerSkuRate,
  useUpdateCustomerManagerSkuVisibility,
} from '../api/customerManagerWorkspace.ts';
import {
  pendingDiscountsByTargetId,
  pendingTransfersByOrgId,
  useMyPendingCustomerTransfers,
  useMyPendingDiscountRequests,
} from '../api/approvals.ts';
import { usePricing } from '../api/pricing.ts';
import {
  CUSTOMER_MANAGER_ROLES,
  MODEL_PLAN_MANAGE_ROLES,
  MODEL_PLAN_REBIND_ROLES,
  RECEIPT_SUBMIT_ROLES,
  ROLE_OPS_ADMIN,
  ROLE_OPS_CUSTOMER_MANAGER,
  SALES_COST_MANAGE_ROLES,
  SERVICE_LEVEL_ASSIGN_ROLES,
  TENANT_ROUTING_ASSIGN_ROLES,
  hasAnyOpsRole,
} from '../auth/opsRoles.ts';
import { AccountLifecycleCard } from '../components/AccountLifecycleCard.tsx';
import {
  isTimelineUnsampled,
  LifecycleSamplingMarker,
} from '../components/LifecycleSamplingMarker.tsx';
import { BrandScopeBanner } from '../components/BrandScopeBanner.tsx';
import { ServiceSuspensionCard } from '../components/ServiceSuspensionCard.tsx';
import { BalanceAdjustmentCard } from '../components/BalanceAdjustmentCard.tsx';
import { CustomerCrmPanel } from '../components/CustomerCrmPanel.tsx';
import { OpsUserRef } from '../components/identity/OpsUserRef.tsx';
import { CustomerEgressPolicyCard } from '../components/CustomerEgressPolicyCard.tsx';
import { CustomerServiceLevelCard } from '../components/CustomerServiceLevelCard.tsx';
import { CustomerTaskAdmissionCard } from '../components/CustomerTaskAdmissionCard.tsx';
import { CustomerSessionPolicyCard } from '../components/CustomerSessionPolicyCard.tsx';
import { CustomerCreditSection } from '../components/CustomerCreditSection.tsx';
import { CustomerReceiptsSection } from '../components/CustomerReceiptsSection.tsx';
import { CustomerMoneyStatusSection } from '../components/CustomerMoneyStatusSection.tsx';
import { CustomerTransferRequestDialog } from '../components/CustomerTransferRequestDialog.tsx';
import { EmailVerificationCard } from '../components/EmailVerificationCard.tsx';
import {
  formatCnyCents,
  formatLocalTimestamp,
  formatUsdMicrosOrCents,
  formatUsdMicrosOrCentsAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyDisplayBalance,
  formatUsdMicrosOrCentsAsCnyMetered,
  parseTimestampMs,
  usdCentsToCnyCents,
  usdMicrosToCnyCents,
} from '../components/formatters.ts';
import { LocalDateTimeInput } from '../components/LocalDateTimeInput.tsx';
import { isRatingPending } from '../components/rating-status.ts';
import { StatusChip } from '../components/StatusChip.tsx';
import { TenantCustomRoutesWorkspace } from '../components/TenantCustomRoutesWorkspace.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { ownerIdentityLabel } from '../lib/ownerLabel.ts';
import { NamespaceRateGroup, PlanLockWrap } from './customer360/NamespaceRateGroup.tsx';
import type { NamespaceRateVm } from './customer360/NamespaceRateGroup.tsx';
import { RateLimitsTab } from './customer360/RateLimitsTab.tsx';
import { groupSkusByNamespace } from './customer360/groupSkusByNamespace.ts';
import { UsageTab } from './customer360/UsageTab.tsx';
import {
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  KpiCard,
  PageHeader,
  Pagination,
  Panel,
  Select,
  Tooltip,
  TBody,
  THead,
  Table,
  Tabs,
  TabsList,
  TabsTrigger,
  Td,
  Th,
  Tr,
  buttonClass,
} from '../ui/index.ts';

const REQUEST_PAGE_LIMIT = 50;
const REQUEST_DEFAULT_WINDOW_DAYS = 30;
const FILTER_ANY = '__any__';

type Tab =
  | 'overview'
  | 'usage'
  | 'requests'
  | 'receipts'
  | 'api_keys'
  | 'gift_cards'
  | 'sku_rates'
  | 'service_level'
  | 'custom_routes'
  | 'rate_limits'
  | 'task_admission'
  | 'admin_actions';

const BASE_TABS: readonly { id: Tab; icon: LucideIcon; labelKey: I18nKey }[] = [
  { id: 'overview', icon: LayoutDashboard, labelKey: 'customer_manager_360.tab.overview' },
  { id: 'usage', icon: Activity, labelKey: 'customer_manager_360.tab.usage' },
  { id: 'requests', icon: ReceiptText, labelKey: 'customer_manager_360.tab.requests' },
  // Manual receipt entry + history for this customer (epic #1031 E2-R5,
  // #1063). Server row scope applies: CM sees own submissions only.
  { id: 'receipts', icon: HandCoins, labelKey: 'customer_manager_360.tab.receipts' },
  { id: 'api_keys', icon: KeyRound, labelKey: 'customer_manager_360.tab.api_keys' },
  { id: 'gift_cards', icon: Gift, labelKey: 'customer_manager_360.tab.gift_cards' },
  { id: 'sku_rates', icon: Waypoints, labelKey: 'customer_manager_360.tab.sku_rates' },
] as const;

/**
 * Service-level tab (#1723/#1725, replacing the #1658 custom-routes workspace):
 * same-source with the ops-api gates via `SERVICE_LEVEL_ASSIGN_ROLES` — admin +
 * customer manager, against a projection carrying no routing-config identity.
 * Frontend gating is defense-in-depth: the tab's endpoints are role- and
 * ownership-gated server-side, so a gating miss can never expose more than a
 * 403.
 */
const SERVICE_LEVEL_TABS: readonly { id: Tab; icon: LucideIcon; labelKey: I18nKey }[] = [
  { id: 'service_level', icon: Layers, labelKey: 'customer_manager_360.tab.service_level' },
] as const;

/**
 * Custom-routes tab (#1658, restored admin-only by #1785). The service-level
 * tab is the customer manager's routing surface, not the admin's: an admin
 * still has to be able to point one customer at a specific routing config, and
 * #1742 removed the only place in Customer 360 where that was possible.
 *
 * Gated on `TENANT_ROUTING_ASSIGN_ROLES`, which #1724 narrowed to ops-admin —
 * same-source with the router-wide ops-api gate on `/v1/ops/tenant-routing`,
 * so the tab cannot outlive or outrun the endpoints it calls. Frontend gating
 * is defense-in-depth either way: a gating miss can only ever expose a 403.
 */
const CUSTOM_ROUTES_TABS: readonly { id: Tab; icon: LucideIcon; labelKey: I18nKey }[] = [
  { id: 'custom_routes', icon: Split, labelKey: 'customer_manager_360.tab.custom_routes' },
] as const;

/**
 * Tabs rendered ONLY for ops-admin sessions (#861 PR A, #2222). Frontend
 * gating is defense-in-depth: the endpoints behind these tabs are
 * `requireRoles(ROLE_OPS_ADMIN)` server-side, so a gating miss can never
 * expose more than a 403.
 */
const ADMIN_ONLY_TABS: readonly { id: Tab; icon: LucideIcon; labelKey: I18nKey }[] = [
  { id: 'rate_limits', icon: Timer, labelKey: 'customer_manager_360.tab.rate_limits' },
  { id: 'task_admission', icon: Gauge, labelKey: 'customer_manager_360.tab.task_admission' },
  { id: 'admin_actions', icon: ShieldAlert, labelKey: 'customer_manager_360.tab.admin_actions' },
] as const;

function defaultRequestScope(now = Date.now()): CustomerManagerRequestLogScope {
  return {
    limit: REQUEST_PAGE_LIMIT,
    offset: 0,
    from: new Date(now - REQUEST_DEFAULT_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString(),
    to: new Date(now).toISOString(),
    status: '',
    lifecycleStatus: '',
    requestId: '',
    apiKeyId: '',
    channelId: '',
    channelSlug: '',
  };
}

function statusFromSelect(value: string): CustomerManagerRequestLogScope['status'] {
  return value === 'success' || value === 'error' ? value : '';
}

function fmtInt(value: number): string {
  return value.toLocaleString('en-US');
}

function fmtRate(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(4) : '—';
}

function formatDuration(ms: number | null): string {
  return typeof ms === 'number' && Number.isFinite(ms) ? `${ms}ms` : '—';
}

function formatPercent(value: number): string {
  return Number.isFinite(value) ? `${value.toFixed(1)}%` : '—';
}

function percentOf(part: number, total: number): string {
  if (!Number.isFinite(part) || !Number.isFinite(total) || total <= 0) {
    return '0%';
  }
  return `${Math.min(100, Math.max(0, (part / total) * 100)).toFixed(3)}%`;
}

/**
 * #1958: these used to coalesce a missing display rate to 0. The amount text
 * printed `—` (the formatters are honest) while the bar next to it rendered a
 * zero-length fill, so a customer owing thousands looked settled to any role
 * that cannot read `/v1/ops/pricing`. A missing rate is now propagated as
 * `null` and rendered as missing, not as zero.
 */
function usdMicrosOrCentsToCnyCents(
  usdMicros: number | null | undefined,
  usdCents: number,
  cnyPerUsd: number | null,
): number | null {
  if (typeof usdMicros === 'number' && Number.isFinite(usdMicros) && usdMicros !== 0) {
    return usdMicrosToCnyCents(usdMicros, cnyPerUsd);
  }
  return usdCentsToCnyCents(usdCents, cnyPerUsd);
}

function truncate(value: string, head = 10, tail = 6): string {
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

function ownerLabel(
  detail: CustomerManagerCustomer360Response,
  translate: (key: I18nKey) => string,
): string {
  return ownerIdentityLabel(
    detail.customer.owner,
    detail.customer.owner.login_methods,
    translate,
  );
}

function randomExternalId(): string {
  if (globalThis.crypto !== undefined && 'randomUUID' in globalThis.crypto) {
    return globalThis.crypto.randomUUID();
  }
  return `cm-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function errorText(error: Error): string {
  return error instanceof OpsApiError ? `${error.code}: ${error.message}` : error.message;
}

export function CustomerManagerCustomer360Page(): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const { orgId: rawOrgId } = useParams<{ orgId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const orgId = rawOrgId ?? null;
  const isAdmin = hasAnyOpsRole(user?.roles ?? [], [ROLE_OPS_ADMIN]);
  // #1958: entry to this page is CUSTOMER_READ_ROLES, which now admits the
  // finance manager (stage-2 signer of a credit application). Entry is not
  // permission: every non-GET under /v1/ops/customers and
  // /v1/ops/v2/customer-manager stays on CUSTOMER_MANAGER_ROLES server-side,
  // so a control rendered for a finance session could only ever 403. One
  // floor for the whole page, read from the same constant the server gates
  // on; the narrower flags below still apply on top of it.
  const canWrite = hasAnyOpsRole(user?.roles ?? [], CUSTOMER_MANAGER_ROLES);
  // Per-customer service kill switch (epic #1033 E4-C7, #1131): admin + same-
  // brand sales lead; the toggle card renders on the overview. CMs never see
  // it (and the ops-api route denies them).
  const canManageServiceSuspension = hasAnyOpsRole(user?.roles ?? [], SALES_COST_MANAGE_ROLES);
  // Service-level tab: admin + customer manager; ownership of the org is
  // proven server-side on the read and the assign call, so the guard here
  // stays role-only.
  const canAssignServiceLevel = hasAnyOpsRole(user?.roles ?? [], SERVICE_LEVEL_ASSIGN_ROLES);
  // Custom-routes tab (#1785): the admin's direct routing-config assignment,
  // beside the service-level tab rather than instead of it. Ownership of the
  // org is proven server-side on the assign call, so the guard here is
  // role-only.
  const canUseCustomRoutes = hasAnyOpsRole(user?.roles ?? [], TENANT_ROUTING_ASSIGN_ROLES);
  const canRebindPlan = hasAnyOpsRole(user?.roles ?? [], MODEL_PLAN_REBIND_ROLES);
  const canManagePlan = hasAnyOpsRole(user?.roles ?? [], MODEL_PLAN_MANAGE_ROLES);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const initialRequestScope = useMemo(() => defaultRequestScope(), []);
  const [requestDraft, setRequestDraft] = useState<CustomerManagerRequestLogScope>(initialRequestScope);
  const [requestScope, setRequestScope] = useState<CustomerManagerRequestLogScope>(initialRequestScope);
  const detailQuery = useCustomerManagerCustomer360(orgId);
  const refreshDetail = useCallback(async (): Promise<void> => {
    const result = await detailQuery.refetch();
    // TanStack Query resolves refetch() with an error result by default. Turn
    // that into a rejected promise so convergence state is cleared only after
    // a verified successful customer-detail read.
    if (!result.isSuccess || result.data === undefined) {
      throw result.error instanceof Error
        ? result.error
        : new Error('customer detail refresh did not succeed');
    }
  }, [detailQuery.refetch]);
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  const requestLogs = useCustomerManagerRequestLogs(orgId, requestScope, activeTab === 'requests');

  const detail = detailQuery.data ?? null;
  const customer = detail?.customer ?? null;

  // Customer transfer/release pilot (epic #1030 E1-S5, #1041): the
  // request entry point renders only for the customer's CURRENT owner —
  // a lead's brand-wide read scope does not grant transfer rights over
  // another manager's book (ops-api re-enforces this server-side).
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const canRequestTransfer =
    customer !== null &&
    user !== null &&
    customer.owner.ops_user_id === user.opsUserId;
  const pendingTransfers = useMyPendingCustomerTransfers(
    hasAnyOpsRole(user?.roles ?? [], [ROLE_OPS_ADMIN, ROLE_OPS_CUSTOMER_MANAGER]),
  );
  const pendingTransfer =
    orgId !== null
      ? pendingTransfersByOrgId(pendingTransfers.data).get(orgId) ?? null
      : null;
  // Scope-projected sku-rate rows (#1104): the server decides which
  // floor layer this session may see. The label view MUST mirror the
  // same predicate `toSkuRateVms` branches on, so a response that ever
  // lost the discriminant can not label procurement data as a sales
  // floor (or vice versa).
  const orgPlanQuery = useOrgModelPlan(orgId, canRebindPlan && orgId !== null);
  const [discoveredPlanName, setDiscoveredPlanName] = useState<string | null>(null);
  useEffect(() => {
    setDiscoveredPlanName(null);
  }, [orgId]);
  const hostedPlanName =
    orgPlanQuery.data?.model_plan?.name ?? discoveredPlanName;
  const hostedPlanId = orgPlanQuery.data?.model_plan?.id ?? null;
  const lockedByPlan = hostedPlanName !== null;
  const skuRateView: OrgSkuRateView = detail?.sku_rate_view === 'sales' ? 'sales' : 'full';
  const skuRates = useMemo(
    () => (detail === null ? [] : toSkuRateVms(detail)),
    [detail],
  );
  const namespaceRates = useMemo(
    () => (detail === null ? new Map<string, NamespaceRateVm>() : toNamespaceRateVms(detail)),
    [detail],
  );
  const tabs = useMemo(
    () => [
      ...BASE_TABS,
      ...(canAssignServiceLevel ? SERVICE_LEVEL_TABS : []),
      ...(canUseCustomRoutes ? CUSTOM_ROUTES_TABS : []),
      ...(isAdmin ? ADMIN_ONLY_TABS : []),
    ],
    [canAssignServiceLevel, canUseCustomRoutes, isAdmin],
  );

  useEffect(() => {
    const requested = searchParams.get('tab');
    if (requested !== null && requested.length > 0) {
      const match = tabs.find((tab) => tab.id === requested);
      if (match !== undefined) {
        setActiveTab(match.id);
        return;
      }
    }
    // A tab the session may not see (role change, hand-typed ?tab=) falls
    // back to the overview rather than rendering an empty pane.
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab('overview');
    }
  }, [activeTab, tabs, searchParams]);

  function selectTab(tab: Tab): void {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === 'overview') {
      next.delete('tab');
    } else {
      next.set('tab', tab);
    }
    setSearchParams(next, { replace: true });
  }

  function selectTabValue(value: string): void {
    const match = tabs.find((tab) => tab.id === value);
    if (match !== undefined) {
      selectTab(match.id);
    }
  }

  function applyRequestFilters(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setRequestScope({ ...requestDraft, offset: 0 });
  }

  function pageRequests(nextOffset: number): void {
    setRequestDraft((prev) => ({ ...prev, offset: nextOffset }));
    setRequestScope((prev) => ({ ...prev, offset: nextOffset }));
  }

  function resetRequestFilters(): void {
    const next = defaultRequestScope();
    setRequestDraft(next);
    setRequestScope(next);
  }

  function showRequestErrors(): void {
    const next: CustomerManagerRequestLogScope = { ...requestDraft, status: 'error', offset: 0 };
    setRequestDraft(next);
    setRequestScope(next);
  }

  if (orgId === null) {
    return <MissingOrg />;
  }

  return (
    <section data-testid="customer-manager-customer-360-page" className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <Link
          to="/crm"
          className="inline-flex w-fit items-center gap-2 text-dense text-paper-low transition-colors duration-120 hover:text-signal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
        >
          <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
          {t('customer_manager_360.back')}
        </Link>
        <PageHeader
          title={<span className="break-words">{customer?.email ?? orgId}</span>}
          sub={
            <>
              <span>{t('customer_manager_360.kicker')}</span>
              <span> · </span>
              <span className="font-mono">{orgId}</span>
              {customer !== null ? <span> · {customer.org_name}</span> : null}
            </>
          }
          actions={
            // The old "Account detail" link is gone with #861 PR B —
            // /customers/accounts/:accountId now resolves straight back to
            // this page, so linking there from here would be a no-op bounce.
            <>
              {/* Server-determined scope chip (#1040), mounted here by #1958
                  so a session that reads cross-brand is told so on the one
                  page it is allowed to open — this is where the 'finance'
                  branch of the banner is reachable. */}
              <BrandScopeBanner testIdPrefix="customer-manager-360" />
              {/* Per-customer entry into the scoped usage overview
                  (epic #1035 E6-D4, #1068). */}
              {customer !== null ? (
                <Link
                  to={`/crm/usage?accountId=${encodeURIComponent(customer.account_id)}`}
                  className={buttonClass('ghost', 'sm')}
                  data-testid="customer-manager-360-usage-link"
                >
                  <ReceiptText aria-hidden="true" className="h-3.5 w-3.5" />
                  {t('customer_manager_360.usage_link')}
                </Link>
              ) : null}
              {pendingTransfer !== null ? (
                <StatusChip
                  variant="warning"
                  label={t('customer_transfer.pending_chip')}
                  testId="customer-manager-360-transfer-pending"
                />
              ) : null}
              {canRequestTransfer ? (
                <Button
                  variant="secondary"
                  onClick={() => setTransferDialogOpen(true)}
                  disabled={pendingTransfer !== null}
                  data-testid="customer-manager-360-transfer-request"
                >
                  <ArrowLeftRight aria-hidden="true" className="h-3.5 w-3.5" />
                  {t('customer_transfer.action')}
                </Button>
              ) : null}
              <Button
                onClick={() => {
                  void detailQuery.refetch();
                  if (activeTab === 'requests') void requestLogs.refetch();
                }}
                data-testid="customer-manager-360-refresh"
              >
                <RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />
                {t('common.refresh')}
              </Button>
            </>
          }
        />
      </div>

      {detailQuery.isPending ? <LoadingPanel /> : null}
      {detailQuery.isError ? (
        <ErrorBanner
          testId="customer-manager-360-error"
          title={t('customer_manager_360.error')}
          error={detailQuery.error}
          onRetry={() => void detailQuery.refetch()}
        />
      ) : null}

      {detail !== null ? (
        <>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            <KpiCard
              label={t('customer_manager_360.metric.balance')}
              value={formatUsdMicrosOrCentsAsCnyDisplayBalance(
                detail.account.balance_usd_micros,
                detail.account.balance_usd_cents,
                cnyPerUsd,
              )}
            />
            <KpiCard label={t('customer_manager_360.metric.requests')} value={fmtInt(detail.customer.usage.request_count)} />
            <KpiCard
              label={t('customer_manager_360.metric.cost')}
              value={formatUsdMicrosOrCentsAsCnyDisplay(
                detail.customer.usage.cost_usd_micros,
                detail.customer.usage.cost_usd_cents,
                cnyPerUsd,
                false)}
            />
            <KpiCard label={t('customer_manager_360.metric.active_keys')} value={fmtInt(detail.customer.api_keys.active)} />
            <KpiCard label={t('customer_manager_360.metric.errors')} value={fmtInt(detail.customer.usage.error_count)} />
          </div>

          <CustomerCreditSection
            orgId={orgId}
            customerEmail={detail.customer.email}
            canWrite={canWrite}
          />

          <Tabs value={activeTab} onValueChange={selectTabValue}>
            <TabsList
              ariaLabel={t('customer_manager_360.tabs_aria')}
              className="max-w-full overflow-x-auto"
            >
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    testId={`customer-manager-360-tab-${tab.id}`}
                  >
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                      <Icon aria-hidden="true" className="h-3.5 w-3.5" />
                      {t(tab.labelKey)}
                    </span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>

          {activeTab === 'overview' ? (
            <OverviewTab detail={detail} cnyPerUsd={cnyPerUsd} showOwner={isAdmin} />
          ) : null}
          {activeTab === 'overview' ? <CustomerMoneyStatusSection orgId={orgId} /> : null}
          {activeTab === 'overview' && canManageServiceSuspension && detail.sku_rate_view === 'full' ? (
            <ServiceSuspensionCard
              orgId={orgId}
              customerEmail={detail.customer.email}
              serviceSuspended={detail.account.service_suspended}
            />
          ) : null}
          {activeTab === 'overview' ? <CustomerCrmPanel orgId={orgId} canWrite={canWrite} /> : null}
          {activeTab === 'usage' ? <UsageTab accountId={detail.customer.account_id} /> : null}
          {activeTab === 'requests' ? (
            <RequestsTab
              detail={detail}
              draft={requestDraft}
              logs={requestLogs}
              cnyPerUsd={cnyPerUsd}
              orgId={orgId}
              showAdminLogLink={isAdmin}
              onDraftChange={setRequestDraft}
              onSubmit={applyRequestFilters}
              onReset={resetRequestFilters}
              onShowErrors={showRequestErrors}
              onPage={pageRequests}
            />
          ) : null}
          {activeTab === 'receipts' ? (
            <CustomerReceiptsSection
              orgId={orgId}
              customerEmail={detail.customer.email}
              customerBrand={detail.customer.signup_brand}
              // Not `canWrite`: the submit route's gate is the receipt
              // console's own `RECEIPT_SUBMIT_ROLES` (v1-ops-receipts.ts
              // `submitGate`), not the customer-prefix write catch-all. The
              // two sets happen to be equal today; reading the one the
              // server actually gates on keeps them from drifting silently.
              canSubmit={hasAnyOpsRole(user?.roles ?? [], RECEIPT_SUBMIT_ROLES)}
            />
          ) : null}
          {activeTab === 'api_keys' ? (
            <ApiKeysTab detail={detail} orgId={orgId} canWrite={canWrite} />
          ) : null}
          {activeTab === 'gift_cards' ? (
            <GiftCardsTab
              detail={detail}
              orgId={orgId}
              cnyPerUsd={cnyPerUsd}
              canWrite={canWrite}
            />
          ) : null}
          {activeTab === 'sku_rates' && orgId !== null ? (
            <SkuRatesTab
              rates={skuRates}
              namespaceRates={namespaceRates}
              view={skuRateView}
              orgId={orgId}
              canWrite={canWrite}
              lockedByPlan={lockedByPlan}
              hostedPlanName={hostedPlanName}
              hostedPlanId={hostedPlanId}
              canRebindPlan={canRebindPlan}
              canManagePlan={canManagePlan}
              onManagedLock={(name) => {
                setDiscoveredPlanName(name);
              }}
              onRefresh={refreshDetail}
            />
          ) : null}
          {activeTab === 'service_level' && canAssignServiceLevel && orgId !== null ? (
            <>
              <CustomerServiceLevelCard
                orgId={orgId}
                testId="customer-manager-360-service-level"
              />
              <CustomerEgressPolicyCard
                orgId={orgId}
                email={customer?.email ?? null}
                testId="customer-manager-360-egress-policy"
              />
            </>
          ) : null}
          {activeTab === 'custom_routes' && canUseCustomRoutes ? (
            <TenantCustomRoutesWorkspace
              key={orgId}
              orgId={orgId}
              testId="customer-manager-360-custom-routes"
            />
          ) : null}
          {activeTab === 'rate_limits' && isAdmin && orgId !== null ? (
            <RateLimitsTab
              orgId={orgId}
              skus={skuRates.map((row) => row.sku)}
            />
          ) : null}
          {activeTab === 'task_admission' && isAdmin ? (
            <CustomerTaskAdmissionCard
              orgId={orgId}
              testId="customer-manager-360-task-admission"
              readOnly
            />
          ) : null}
          {activeTab === 'admin_actions' && isAdmin && detail.sku_rate_view === 'full' ? (
            <AdminActionsTab detail={detail} />
          ) : null}
        </>
      ) : null}

      {customer !== null ? (
        <CustomerTransferRequestDialog
          open={transferDialogOpen}
          onOpenChange={setTransferDialogOpen}
          orgId={orgId}
          customerEmail={customer.email}
          customerBrand={customer.signup_brand}
        />
      ) : null}
    </section>
  );
}

function MissingOrg(): JSX.Element {
  const t = useT();
  return (
    <section
      data-testid="customer-manager-360-missing"
      className="rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3 text-body text-bad-bright"
    >
      {t('customer_manager_360.missing_org')}
    </section>
  );
}

/**
 * Admin-only account controls migrated from AccountDetail (#861 PR A):
 * balance adjustment (type-to-confirm), email verification support
 * actions, and the suspend / unsuspend / soft-delete lifecycle card
 * (type-to-confirm). Rendered only behind the `admin_actions` tab, which
 * itself only exists for ops-admin sessions; every underlying mutation
 * targets `/v1/ops/admin/*` (server-enforced ROLE_OPS_ADMIN).
 */
// Admin-only surface: admin sessions always receive the full projection,
// so the cards below can keep their `GetAccountDetailResponse` contract.
function AdminActionsTab({
  detail,
}: {
  readonly detail: CustomerManagerCustomer360FullResponse;
}): JSX.Element {
  const t = useT();
  const account = detail.account;
  return (
    <div data-testid="customer-manager-360-admin-actions" className="flex flex-col gap-4">
      <p
        className="m-0 rounded-lg border border-warn-line bg-warn-dim px-4 py-3 text-dense text-warn"
        data-testid="customer-manager-360-admin-actions-hint"
      >
        {t('customer_manager_360.admin.hint')}
      </p>
      <CustomerSessionPolicyCard
        orgId={detail.customer.org_id}
        testId="customer-session-policy-card"
      />
      <BalanceAdjustmentCard account={account} />
      <EmailVerificationCard account={account} />
      {account.is_system ? null : (
        <AccountLifecycleCard
          accountId={account.account_id}
          email={account.email}
          suspendedAt={account.suspended_at}
          suspensionReason={account.suspension_reason}
          deletedAt={account.deleted_at}
        />
      )}
    </div>
  );
}

function OverviewTab({
  detail,
  cnyPerUsd,
  showOwner,
}: {
  readonly detail: CustomerManagerCustomer360Response;
  readonly cnyPerUsd: number | null;
  readonly showOwner: boolean;
}): JSX.Element {
  const t = useT();
  const account = detail.account;
  const usage = detail.customer.usage;
  const errors = usage.error_count;
  const successes = Math.max(0, usage.request_count - errors);
  const successRate = usage.request_count > 0 ? (successes / usage.request_count) * 100 : 0;
  const activeKeys = detail.customer.api_keys.active;
  const revokedKeys = detail.customer.api_keys.revoked;
  const keyTotal = Math.max(1, detail.customer.api_keys.total);
  const usageCostCnyCents = usdMicrosOrCentsToCnyCents(
    usage.cost_usd_micros,
    usage.cost_usd_cents,
    cnyPerUsd,
  );
  const balanceCnyCents = usdMicrosOrCentsToCnyCents(
    account.balance_usd_micros,
    account.balance_usd_cents,
    cnyPerUsd,
  );
  // Only the amounts we actually have participate in the shared scale; a bar
  // with no amount renders no fill at all (see MoneyBar).
  const moneyBarMaxCnyCents = Math.max(
    1,
    ...[balanceCnyCents, usageCostCnyCents].filter((value): value is number => value !== null),
  );
  return (
    <div data-testid="customer-manager-360-overview" className="grid gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.75fr)]">
      <Panel
        testId="customer-manager-360-dashboard-money-chart"
        caption={t('customer_manager_360.overview.money_dashboard')}
        padded
      >
        <div className="flex flex-col gap-4">
          <p className="m-0 text-dense text-paper-low">
            {t('customer_manager_360.overview.money_dashboard_hint')}
          </p>
          <div className="grid gap-3 lg:grid-cols-2">
            <MoneyBar
              testId="customer-manager-360-money-balance"
              label={t('customer_manager_360.metric.balance')}
              value={formatUsdMicrosOrCentsAsCnyDisplayBalance(
                account.balance_usd_micros,
                account.balance_usd_cents,
                cnyPerUsd,
              )}
              amountCnyCents={balanceCnyCents}
              maxCnyCents={moneyBarMaxCnyCents}
            />
            <MoneyBar
              testId="customer-manager-360-money-cost"
              label={t('customer_manager_360.metric.cost')}
              value={formatUsdMicrosOrCentsAsCnyDisplay(usage.cost_usd_micros, usage.cost_usd_cents, cnyPerUsd, false)}
              amountCnyCents={usageCostCnyCents}
              maxCnyCents={moneyBarMaxCnyCents}
            />
          </div>
        </div>
      </Panel>

      <Panel
        testId="customer-manager-360-dashboard-usage-chart"
        caption={t('customer_manager_360.overview.usage_dashboard')}
        padded
      >
        <div className="flex flex-col gap-4">
          <p className="m-0 text-dense text-paper-low">
            {t('customer_manager_360.overview.usage_dashboard_hint')}
          </p>
          <div className="flex items-end justify-between gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-label text-paper-low">{t('customer_manager_360.overview.success_rate')}</span>
              <span className="font-mono text-stat text-paper">{formatPercent(successRate)}</span>
            </div>
            <StatusChip variant={errors > 0 ? 'warning' : 'ok'} label={errors > 0 ? `${errors} ${t('customer_manager_360.overview.errors')}` : t('customer_manager_360.risk.none')} />
          </div>
          <div className="h-4 overflow-hidden rounded bg-well">
            <div className="flex h-full">
              <div className="bg-ok" style={{ width: percentOf(successes, usage.request_count) }} />
              <div className="bg-bad" style={{ width: percentOf(errors, usage.request_count) }} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <KeyValue label={t('customer_manager_360.overview.successful_requests')} value={fmtInt(successes)} />
            <KeyValue label={t('customer_manager_360.metric.requests')} value={fmtInt(usage.request_count)} />
          </div>
          <div className="flex flex-col gap-2 rounded-lg bg-well px-3 py-3">
            <div className="flex items-center justify-between gap-3 text-dense">
              <span className="text-paper-low">{t('customer_manager_360.overview.key_posture')}</span>
              <span className="font-mono text-paper">{activeKeys}/{detail.customer.api_keys.total}</span>
            </div>
            <div className="h-2 overflow-hidden rounded bg-canvas">
              <div className="flex h-full">
                <div className="bg-ok" style={{ width: percentOf(activeKeys, keyTotal) }} />
                <div className="bg-bad" style={{ width: percentOf(revokedKeys, keyTotal) }} />
              </div>
            </div>
          </div>
        </div>
      </Panel>

      <Panel caption={t('customer_manager_360.overview.account')} padded>
        <div className="flex flex-col gap-4">
          <p className="m-0 text-dense text-paper-low">
            {t('customer_manager_360.overview.account_dashboard_hint')}
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            {showOwner ? (
              <>
                <KeyValue label={t('customer_manager_360.field.owner')} value={ownerLabel(detail, t)} />
                <KeyValue label={t('customer_manager_360.field.assigned_at')} value={formatLocalTimestamp(detail.customer.owner.assigned_at)} />
              </>
            ) : null}
            <KeyValue label={t('customer_manager_360.field.created_at')} value={formatLocalTimestamp(account.created_at)} />
            <KeyValue label={t('customer_manager_360.field.verified')} value={account.email_verified ? t('common.yes') : t('common.no')} />
            <KeyValue label={t('customer_manager_360.field.suspended')} value={formatLocalTimestamp(account.suspended_at)} />
            <KeyValue label={t('customer_manager_360.field.deleted')} value={formatLocalTimestamp(account.deleted_at)} />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-label text-paper-low">{t('customer_manager_360.overview.risks')}</span>
            {detail.customer.risk_signals.length === 0 ? (
              <StatusChip variant="ok" label={t('customer_manager_360.risk.none')} />
            ) : (
              detail.customer.risk_signals.map((signal) => (
                <div key={signal.code} className="flex items-start justify-between gap-3 rounded-lg bg-well px-3 py-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-body font-medium text-paper">{signal.label}</span>
                    <span className="text-dense text-paper-low">{signal.detail}</span>
                  </div>
                  <StatusChip
                    variant={signal.severity === 'critical' ? 'error' : signal.severity === 'warning' ? 'warning' : 'neutral'}
                    label={signal.severity}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </Panel>

      <Panel
        testId="customer-manager-360-dashboard-transaction-flow"
        caption={t('customer_manager_360.overview.transactions')}
        padded
      >
        <div className="flex flex-col gap-4">
          <p className="m-0 text-dense text-paper-low">
            {t('customer_manager_360.overview.transaction_dashboard_hint')}
          </p>
          {account.recent_transactions.length === 0 ? (
            <p className="m-0 text-body text-paper-low">{t('customer_manager_360.empty.transactions')}</p>
          ) : (
            <div className="flex flex-col gap-2">
              {account.recent_transactions.map((tx) => (
                <div key={tx.id} className="grid gap-3 rounded-lg bg-well px-3 py-3 text-dense md:grid-cols-[1fr_auto]">
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="truncate font-medium text-paper">{tx.reason ?? tx.type}</span>
                    <span className="font-mono text-paper-low">{formatLocalTimestamp(tx.created_at)}</span>
                  </div>
                  <span className="font-mono text-body font-semibold text-paper">
                    {formatUsdMicrosOrCents(tx.amount_usd_micros, tx.amount_usd_cents, true)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Panel>

      <Panel
        testId="customer-manager-360-dashboard-lifecycle-rail"
        caption={t('customer_manager_360.overview.lifecycle')}
        padded
        className="xl:col-span-2"
      >
        <div className="flex flex-col gap-4">
          <p className="m-0 text-dense text-paper-low">
            {t('customer_manager_360.overview.lifecycle_dashboard_hint')}
          </p>
          {detail.recent_requests.length === 0 ? (
            <p className="m-0 text-body text-paper-low">{t('customer_manager_360.empty.lifecycle')}</p>
          ) : (
            <div className="grid gap-2 lg:grid-cols-2">
              {detail.recent_requests.map((row) => (
                <LifecycleRow key={row.request_id} row={row} />
              ))}
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}

function RequestsTab({
  detail,
  draft,
  logs,
  cnyPerUsd,
  orgId,
  showAdminLogLink,
  onDraftChange,
  onSubmit,
  onReset,
  onShowErrors,
  onPage,
}: {
  readonly detail: CustomerManagerCustomer360Response;
  readonly draft: CustomerManagerRequestLogScope;
  readonly logs: ReturnType<typeof useCustomerManagerRequestLogs>;
  readonly cnyPerUsd: number | null;
  readonly orgId: string;
  readonly showAdminLogLink: boolean;
  readonly onDraftChange: (scope: CustomerManagerRequestLogScope) => void;
  readonly onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  readonly onReset: () => void;
  readonly onShowErrors: () => void;
  readonly onPage: (offset: number) => void;
}): JSX.Element {
  const t = useT();
  const rows = logs.data?.rows ?? [];
  const total = logs.data?.total ?? 0;
  const currentOffset = logs.data?.offset ?? draft.offset;
  const [openLifecycleRequestIds, setOpenLifecycleRequestIds] = useState<ReadonlySet<string>>(() => new Set());
  const visibleRequestIds = rows.map((row) => row.request_id);
  const openVisibleLifecycleCount = visibleRequestIds.filter((requestId) => openLifecycleRequestIds.has(requestId)).length;
  const traceAvailableCount = rows.filter((row) => row.trace_available === true || row.trace_available === 1).length;

  function expandVisibleLifecycle(): void {
    setOpenLifecycleRequestIds(new Set(visibleRequestIds));
  }

  function collapseVisibleLifecycle(): void {
    setOpenLifecycleRequestIds(new Set());
  }

  function toggleLifecycle(requestId: string): void {
    setOpenLifecycleRequestIds((current) => {
      const next = new Set(current);
      if (next.has(requestId)) {
        next.delete(requestId);
      } else {
        next.add(requestId);
      }
      return next;
    });
  }

  return (
    <section data-testid="customer-manager-360-requests" className="flex flex-col gap-4 rounded-lg border border-line-soft bg-card p-4">
      <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <LocalDateTimeInput
          label={t('customer_manager_360.filter.from')}
          value={draft.from}
          onChange={(v) => onDraftChange({ ...draft, from: v })}
          testId="customer-manager-360-filter-from"
        />
        <LocalDateTimeInput
          label={t('customer_manager_360.filter.to')}
          value={draft.to}
          onChange={(v) => onDraftChange({ ...draft, to: v })}
          testId="customer-manager-360-filter-to"
        />
        <TextField
          fieldId="customer-manager-360-filter-request-id"
          label={t('customer_manager_360.filter.request_id')}
          value={draft.requestId}
          onChange={(v) => onDraftChange({ ...draft, requestId: v })}
        />
        <FormField
          label={t('customer_manager_360.filter.status')}
          fieldId="customer-manager-360-filter-status"
        >
          <Select
            value={draft.status === '' ? FILTER_ANY : draft.status}
            onValueChange={(v) => onDraftChange({ ...draft, status: statusFromSelect(v) })}
            options={[
              { value: FILTER_ANY, label: t('customer_manager_360.filter.any') },
              { value: 'success', label: t('customer_manager_360.filter.success') },
              { value: 'error', label: t('customer_manager_360.filter.error') },
            ]}
            ariaLabel={t('customer_manager_360.filter.status')}
          />
        </FormField>
        <TextField
          fieldId="customer-manager-360-filter-lifecycle-status"
          label={t('customer_manager_360.filter.lifecycle_status')}
          value={draft.lifecycleStatus}
          onChange={(v) => onDraftChange({ ...draft, lifecycleStatus: v })}
        />
        <FormField
          label={t('customer_manager_360.filter.api_key')}
          fieldId="customer-manager-360-filter-api-key"
        >
          <Select
            value={draft.apiKeyId === '' ? FILTER_ANY : draft.apiKeyId}
            onValueChange={(v) => onDraftChange({ ...draft, apiKeyId: v === FILTER_ANY ? '' : v })}
            options={[
              { value: FILTER_ANY, label: t('customer_manager_360.filter.any') },
              ...detail.api_keys.map((key) => ({
                value: key.id,
                label: key.name ?? key.key_prefix ?? truncate(key.id, 8, 4),
              })),
            ]}
            ariaLabel={t('customer_manager_360.filter.api_key')}
          />
        </FormField>
        <TextField
          fieldId="customer-manager-360-filter-channel-slug"
          label={t('customer_manager_360.filter.channel_slug')}
          value={draft.channelSlug}
          onChange={(v) => onDraftChange({ ...draft, channelSlug: v })}
        />
        <div className="flex items-end justify-end gap-2 md:col-span-2 xl:col-span-4">
          <Button onClick={onReset}>
            <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
            {t('customer_manager_360.filter.reset_30d')}
          </Button>
          <Button type="submit" variant="primary">
            <Filter aria-hidden="true" className="h-3.5 w-3.5" />
            {t('customer_manager_360.filter.apply')}
          </Button>
        </div>
      </form>

      <div
        data-testid="customer-manager-360-request-log-controls"
        className="flex flex-col gap-3 border-t border-line-soft pt-3 xl:flex-row xl:items-center xl:justify-between"
      >
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <h3 className="m-0 text-label font-medium text-paper">
              {t('customer_manager_360.logs.controls.title')}
            </h3>
            <span className="rounded-full bg-raised px-2 py-0.5 text-micro font-medium text-paper-low">
              {t('customer_manager_360.logs.controls.visible', {
                count: rows.length,
                trace: traceAvailableCount,
              })}
            </span>
          </div>
          <p className="m-0 text-dense text-paper-low">{t('customer_manager_360.logs.controls.hint')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <LogControlButton
            icon={RefreshCw}
            label={t('customer_manager_360.logs.controls.refresh')}
            testId="customer-manager-360-log-control-refresh"
            disabled={logs.isFetching}
            onClick={() => void logs.refetch()}
          />
          <LogControlButton
            icon={CircleAlert}
            label={t('customer_manager_360.logs.controls.errors_only')}
            testId="customer-manager-360-log-control-errors"
            active={draft.status === 'error'}
            onClick={onShowErrors}
          />
          <LogControlButton
            icon={ChevronsUpDown}
            label={t('customer_manager_360.logs.controls.expand')}
            testId="customer-manager-360-log-control-expand"
            disabled={rows.length === 0 || openVisibleLifecycleCount === rows.length}
            onClick={expandVisibleLifecycle}
          />
          <LogControlButton
            icon={ChevronsDownUp}
            label={t('customer_manager_360.logs.controls.collapse')}
            testId="customer-manager-360-log-control-collapse"
            disabled={openVisibleLifecycleCount === 0}
            onClick={collapseVisibleLifecycle}
          />
        </div>
      </div>

      {logs.isPending ? <LoadingPanel /> : null}
      {logs.isError ? (
        <ErrorBanner
          testId="customer-manager-360-requests-error"
          title={t('customer_manager_360.requests.error')}
          error={logs.error}
          onRetry={() => void logs.refetch()}
        />
      ) : null}
      {logs.isSuccess ? (
        <>
          <RequestTable
            rows={rows}
            cnyPerUsd={cnyPerUsd}
            orgId={orgId}
            showAdminLogLink={showAdminLogLink}
            openLifecycleRequestIds={openLifecycleRequestIds}
            onLifecycleToggle={toggleLifecycle}
          />
          <Pagination
            testId="customer-manager-360-requests-pagination"
            ariaLabel={t('customer_manager_360.pagination.aria_label')}
            page={Math.floor(currentOffset / REQUEST_PAGE_LIMIT) + 1}
            pageCount={Math.max(1, Math.ceil(total / REQUEST_PAGE_LIMIT))}
            onPageChange={(page) => onPage((page - 1) * REQUEST_PAGE_LIMIT)}
            prevLabel={t('common.previous')}
            nextLabel={t('common.next')}
            full={{
              summary: t('customer_manager_360.pagination.range', {
                start: total === 0 ? 0 : currentOffset + 1,
                end: Math.min(currentOffset + rows.length, total),
                total,
              }),
              firstLabel: t('common.pagination.first'),
              lastLabel: t('common.pagination.last'),
              skipPrevLabel: t('common.pagination.skip_prev'),
              skipNextLabel: t('common.pagination.skip_next'),
              jumpAriaLabel: t('common.pagination.jump'),
              jumpSubmitLabel: t('common.pagination.jump_submit'),
              pageLabel: (page) => t('common.pagination.page', { page }),
              busy: logs.isFetching,
            }}
          />
        </>
      ) : null}
    </section>
  );
}

function LogControlButton({
  icon: Icon,
  label,
  testId,
  active = false,
  disabled = false,
  onClick,
}: {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly testId: string;
  readonly active?: boolean;
  readonly disabled?: boolean;
  readonly onClick: () => void;
}): JSX.Element {
  return (
    <Button
      size="sm"
      variant={active ? 'danger' : 'secondary'}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
    >
      <Icon aria-hidden="true" className="h-3.5 w-3.5" />
      {label}
    </Button>
  );
}

function RequestTable({
  rows,
  cnyPerUsd,
  orgId,
  showAdminLogLink,
  openLifecycleRequestIds,
  onLifecycleToggle,
}: {
  readonly rows: readonly CustomerManagerRequestLogRow[];
  readonly cnyPerUsd: number | null;
  readonly orgId: string;
  readonly showAdminLogLink: boolean;
  readonly openLifecycleRequestIds: ReadonlySet<string>;
  readonly onLifecycleToggle: (requestId: string) => void;
}): JSX.Element {
  const t = useT();
  if (rows.length === 0) {
    return <p className="m-0 text-body text-paper-low">{t('customer_manager_360.empty.requests')}</p>;
  }
  return (
    <Table>
      <THead>
        <Tr interactive={false}>
          <Th>{t('customer_manager_360.column.request')}</Th>
          <Th>{t('customer_manager_360.column.api_key')}</Th>
          <Th>{t('customer_manager_360.column.channel')}</Th>
          <Th>{t('customer_manager_360.column.model')}</Th>
          <Th>{t('customer_manager_360.column.status')}</Th>
          <Th num>{t('customer_manager_360.column.cost')}</Th>
          <Th>{t('customer_manager_360.column.created_at')}</Th>
          <Th>{t('customer_manager_360.column.logs')}</Th>
        </Tr>
      </THead>
      <TBody>
        {rows.map((row) => (
          <RequestLogRow
            key={row.id}
            row={row}
            cnyPerUsd={cnyPerUsd}
            orgId={orgId}
            showAdminLogLink={showAdminLogLink}
            lifecycleOpen={openLifecycleRequestIds.has(row.request_id)}
            onLifecycleToggle={() => onLifecycleToggle(row.request_id)}
          />
        ))}
      </TBody>
    </Table>
  );
}

function RequestLogRow({
  row,
  cnyPerUsd,
  orgId,
  showAdminLogLink,
  lifecycleOpen,
  onLifecycleToggle,
}: {
  readonly row: CustomerManagerRequestLogRow;
  readonly cnyPerUsd: number | null;
  readonly orgId: string;
  readonly showAdminLogLink: boolean;
  readonly lifecycleOpen: boolean;
  readonly onLifecycleToggle: () => void;
}): JSX.Element {
  const t = useT();
  const lifecycle = useCustomerManagerRequestLifecycle(orgId, row.request_id, lifecycleOpen);
  const traceAvailable = row.trace_available === true || row.trace_available === 1;
  const lifecycleStatus = row.lifecycle_status ?? '—';
  return (
    <>
      <Tr>
        <Td className="align-top">
          <div className="flex flex-col gap-1">
            <span className="font-mono text-paper">{truncate(row.request_id)}</span>
            <span className="font-mono text-micro text-paper-low">{row.request_id}</span>
          </div>
        </Td>
        <Td className="align-top">{row.api_key_name ?? row.api_key_prefix ?? truncate(row.api_key_id, 8, 4)}</Td>
        <Td className="align-top">{row.channel_slug ?? row.upstream_channel_slug ?? '—'}</Td>
        <Td mono className="align-top">{`${row.vendor}/${row.model}`}</Td>
        <Td className="align-top">
          <div className="flex flex-col items-start gap-1">
            <StatusChip variant={row.status_code >= 400 ? 'error' : 'ok'} label={row.status_code} />
            <span className="font-mono text-micro text-paper-low">{lifecycleStatus}</span>
          </div>
        </Td>
        <Td num className="align-top">
          {isRatingPending(row.rating_status) ? (
            <span className="text-paper-low">{t('customer_manager_360.cost.pending')}</span>
          ) : (
            formatUsdMicrosOrCentsAsCnyMetered(row.cost_usd_micros, row.cost_usd_cents, cnyPerUsd, false)
          )}
        </Td>
        <Td mono className="align-top">{formatLocalTimestamp(row.created_at)}</Td>
        <Td className="align-top">
          <div className="flex min-w-64 flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={onLifecycleToggle}
              data-testid={`customer-manager-request-lifecycle-toggle-${row.id}`}
            >
              {lifecycleOpen ? (
                <ChevronUp aria-hidden="true" className="h-3.5 w-3.5" />
              ) : (
                <Waypoints aria-hidden="true" className="h-3.5 w-3.5" />
              )}
              {lifecycleOpen
                ? t('customer_manager_360.logs.hide_lifecycle')
                : t('customer_manager_360.logs.view_lifecycle')}
            </Button>
            {showAdminLogLink ? (
              <Link
                to={`/customers/requests?requestId=${encodeURIComponent(row.request_id)}`}
                data-testid={`customer-manager-request-open-global-${row.id}`}
                className={buttonClass('ghost', 'sm')}
              >
                <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
                {t('customer_manager_360.logs.open_global')}
              </Link>
            ) : null}
            <StatusChip
              variant={traceAvailable ? 'ok' : 'neutral'}
              label={traceAvailable ? t('customer_manager_360.logs.trace_available') : t('customer_manager_360.logs.trace_unavailable')}
            />
          </div>
        </Td>
      </Tr>
      {lifecycleOpen ? (
        <Tr interactive={false}>
          <td colSpan={8} className="border-b border-line-soft px-4 pb-4">
            <RequestLifecyclePanel
              requestId={row.request_id}
              lifecycle={lifecycle.data}
              loading={lifecycle.isLoading}
              error={lifecycle.error}
            />
          </td>
        </Tr>
      ) : null}
    </>
  );
}

function RequestLifecyclePanel({
  requestId,
  lifecycle,
  loading,
  error,
}: {
  readonly requestId: string;
  readonly lifecycle: CustomerManagerRequestLifecycleResponse | undefined;
  readonly loading: boolean;
  readonly error: unknown;
}): JSX.Element {
  const t = useT();
  if (loading) {
    return (
      <div className="rounded-lg bg-well px-4 py-3 text-dense text-paper-low">
        {t('customer_manager_360.logs.lifecycle_loading')}
      </div>
    );
  }
  if (error !== null && error !== undefined) {
    return (
      <div className="rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3 text-dense text-bad-bright">
        {t('customer_manager_360.logs.lifecycle_error')}: {error instanceof Error ? error.message : 'unknown error'}
      </div>
    );
  }
  if (lifecycle === undefined) {
    return (
      <div className="rounded-lg bg-well px-4 py-3 text-dense text-paper-low">
        {t('customer_manager_360.logs.lifecycle_empty')}
      </div>
    );
  }

  const metric = lifecycle.metric;
  const unsampled = isTimelineUnsampled(lifecycle);
  return (
    <div
      data-testid={`customer-manager-request-lifecycle-${requestId}`}
      className="sticky left-0 flex w-[calc(100vw-6rem)] max-w-[calc(100vw-6rem)] flex-col gap-3 rounded-lg bg-well p-4 lg:w-[calc(100vw-22rem)] lg:max-w-[calc(100vw-22rem)]"
    >
      <div className="flex flex-wrap items-center gap-2 text-dense text-paper-low">
        <span className="font-mono text-paper">{requestId}</span>
        <StatusChip
          variant={metric.statusCode !== null && metric.statusCode >= 400 ? 'error' : 'ok'}
          label={`${metric.lifecycleStatus}${metric.statusCode !== null ? ` ${metric.statusCode}` : ''}`}
        />
        <span className="font-mono">last {metric.lastPhase}</span>
        <LifecycleSamplingMarker
          metric={metric}
          events={lifecycle.events}
          timelineUnsampled={lifecycle.timeline_unsampled}
          testId={`customer-manager-request-lifecycle-unsampled-${requestId}`}
        />
      </div>
      <dl className="grid gap-2 md:grid-cols-2 xl:grid-cols-5">
        <MetricCell label={t('customer_manager_360.logs.metric.total')} value={formatDuration(metric.totalMs)} />
        <MetricCell label={t('customer_manager_360.logs.metric.upstream')} value={formatDuration(metric.upstreamDurationMs)} />
        <MetricCell label={t('customer_manager_360.logs.metric.client')} value={formatDuration(metric.clientResponseDurationMs)} />
        <MetricCell label={t('customer_manager_360.logs.metric.billing')} value={formatDuration(metric.billingDurationMs)} />
        <MetricCell label={t('customer_manager_360.logs.metric.trace')} value={metric.traceStatus ?? '—'} />
      </dl>
      {unsampled ? null : (
        <div className="flex flex-col gap-1">
          <div className="hidden rounded-lg bg-card px-3 py-2 text-label font-medium text-paper-low lg:grid lg:grid-cols-[minmax(170px,1fr)_190px_90px_90px_minmax(160px,1fr)]">
            <span>{t('customer_manager_360.logs.phase')}</span>
            <span>{t('customer_manager_360.logs.event_time')}</span>
            <span>{t('customer_manager_360.logs.elapsed')}</span>
            <span>{t('customer_manager_360.logs.duration')}</span>
            <span>{t('customer_manager_360.logs.outcome')}</span>
          </div>
          <ol className="m-0 flex list-none flex-col gap-1 p-0">
            {lifecycle.events.map((event) => (
              <RequestLifecycleEventRow key={event.eventId} event={event} />
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function RequestLifecycleEventRow({ event }: { readonly event: CustomerManagerRequestLifecycleEvent }): JSX.Element {
  const isError = event.errorCode !== null || (event.statusCode !== null && event.statusCode >= 400);
  const cls = isError
    ? 'border border-bad-line bg-bad-dim/40 text-bad-bright'
    : event.phase === 'completed' || event.phase === 'trace_written'
      ? 'bg-raised text-ok'
      : 'bg-card text-paper-mid';
  return (
    <li className={`grid gap-2 rounded-lg px-3 py-2 text-dense lg:grid-cols-[minmax(170px,1fr)_190px_90px_90px_minmax(160px,1fr)] ${cls}`}>
      <span className="font-mono font-semibold">{event.phase}</span>
      <span className="font-mono" title={event.eventTs}>{formatLocalTimestamp(event.eventTs, { seconds: true, milliseconds: true })}</span>
      <span className="font-mono">+{formatDuration(event.elapsedMs)}</span>
      <span className="font-mono">{formatDuration(event.durationMs)}</span>
      <span className="truncate font-mono" title={event.errorCode ?? event.outcome ?? ''}>
        {event.errorCode ?? event.outcome ?? '—'}
      </span>
    </li>
  );
}

function MetricCell({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="rounded-lg bg-card px-3 py-2">
      <dt className="text-label text-paper-low">{label}</dt>
      <dd className="m-0 mt-1 break-all font-mono text-dense text-paper">{value}</dd>
    </div>
  );
}

function ApiKeysTab({
  detail,
  orgId,
  canWrite,
}: {
  readonly detail: CustomerManagerCustomer360Response;
  readonly orgId: string;
  readonly canWrite: boolean;
}): JSX.Element {
  const t = useT();
  const revoke = useRevokeCustomerManagerApiKey();
  const toast = useToast();
  async function handleRevoke(keyId: string, reason: string): Promise<void> {
    await revoke.mutateAsync({ orgId, keyId, body: { reason } });
    toast.success(t('customer_manager_360.toast.key_revoked'));
  }
  return (
    <section data-testid="customer-manager-360-api-keys" className="flex flex-col gap-3">
      {detail.api_keys.length === 0 ? (
        <p className="m-0 rounded-lg border border-line-soft bg-card p-4 text-body text-paper-low">
          {t('customer_manager_360.empty.api_keys')}
        </p>
      ) : (
        <Table>
          <THead>
            <Tr interactive={false}>
              <Th>{t('customer_manager_360.column.name')}</Th>
              <Th>{t('customer_manager_360.column.prefix')}</Th>
              <Th>{t('customer_manager_360.column.channel')}</Th>
              <Th>{t('customer_manager_360.column.last_used')}</Th>
              <Th>{t('customer_manager_360.column.state')}</Th>
              <Th>{t('customer_manager_360.column.action')}</Th>
            </Tr>
          </THead>
          <TBody>
            {detail.api_keys.map((key) => (
              <ApiKeyRow
                key={key.id}
                apiKey={key}
                isPending={revoke.isPending}
                canWrite={canWrite}
                onRevoke={handleRevoke}
              />
            ))}
          </TBody>
        </Table>
      )}
    </section>
  );
}

function ApiKeyRow({
  apiKey,
  isPending,
  canWrite,
  onRevoke,
}: {
  readonly apiKey: CustomerManagerApiKeyDetail;
  readonly isPending: boolean;
  /** #1958: revocation is a write; a read-only session gets the state only. */
  readonly canWrite: boolean;
  readonly onRevoke: (keyId: string, reason: string) => Promise<void>;
}): JSX.Element {
  const t = useT();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  async function submit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setError(t('customer_manager_360.validation.reason'));
      return;
    }
    try {
      setError(null);
      await onRevoke(apiKey.id, trimmed);
      setReason('');
    } catch (err) {
      setError(errorText(err instanceof Error ? err : new Error(String(err))));
    }
  }
  return (
    <Tr>
      <Td>{apiKey.name ?? '—'}</Td>
      <Td mono>{apiKey.key_prefix ?? '—'}</Td>
      <Td>{apiKey.channel_display_name ?? apiKey.channel_slug ?? '—'}</Td>
      <Td mono>{formatLocalTimestamp(apiKey.last_used_at)}</Td>
      <Td>
        <StatusChip
          variant={apiKey.revoked_at === null ? 'ok' : 'error'}
          label={apiKey.revoked_at === null ? t('customer_manager_360.state.active') : t('customer_manager_360.state.revoked')}
        />
      </Td>
      <Td>
        {apiKey.revoked_at !== null ? (
          <span className="text-dense text-paper-low">{formatLocalTimestamp(apiKey.revoked_at)}</span>
        ) : canWrite ? (
          <form onSubmit={(e) => void submit(e)} className="flex min-w-80 flex-col gap-2">
            <div className="flex gap-2">
              <Input
                value={reason}
                onChange={(e) => setReason(e.currentTarget.value)}
                placeholder={t('customer_manager_360.revoke.reason')}
                aria-label={t('customer_manager_360.revoke.reason')}
                className="min-w-0 flex-1"
              />
              <Button type="submit" variant="danger" disabled={isPending}>
                <Ban aria-hidden="true" className="h-3.5 w-3.5" />
                {t('customer_manager_360.action.revoke')}
              </Button>
            </div>
            {error !== null ? (
              <span role="alert" className="text-label text-bad">{error}</span>
            ) : null}
          </form>
        ) : (
          <span className="text-dense text-paper-low">—</span>
        )}
      </Td>
    </Tr>
  );
}

function GiftCardsTab({
  detail,
  orgId,
  cnyPerUsd,
  canWrite,
}: {
  readonly detail: CustomerManagerCustomer360Response;
  readonly orgId: string;
  readonly cnyPerUsd: number | null;
  /** #1958: issuing and redeeming move money; the lot history stays readable. */
  readonly canWrite: boolean;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const issue = useIssueCustomerManagerGift();
  const redeem = useRedeemCustomerManagerGiftCard();
  const [issueAmount, setIssueAmount] = useState('');
  const [issueReason, setIssueReason] = useState('');
  const [issueResult, setIssueResult] = useState<string | null>(null);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemReason, setRedeemReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Targeted direct-credit gift under the monthly quota gate (#1103):
  // within remaining quota → credited immediately; over quota → a
  // pending gift_over_quota approval for the same-brand lead, and NO
  // money moves until it is approved.
  async function submitIssue(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const yuan = Number(issueAmount);
    if (!Number.isFinite(yuan) || !Number.isInteger(yuan) || yuan <= 0) {
      setFormError(t('customer_manager_360.validation.amount'));
      return;
    }
    try {
      setFormError(null);
      const reason = issueReason.trim();
      const result = await issue.mutateAsync({
        orgId,
        body: {
          amount_cny_cents: Math.round(yuan * 100),
          ...(reason.length > 0 ? { reason } : {}),
          external_request_id: randomExternalId(),
        },
      });
      if (result.outcome === 'issued') {
        setIssueResult(
          t('customer_manager_360.gift.credited_result', {
            amount: formatCnyCents(result.issuance.amount_cny_cents, false),
          }),
        );
        toast.success(t('customer_manager_360.toast.gift_credited'));
      } else {
        setIssueResult(
          t('customer_manager_360.gift.over_quota_result', {
            amount: formatCnyCents(Math.round(yuan * 100), false),
            remaining: formatCnyCents(result.quota.remaining_cny_cents, false),
            quota: formatCnyCents(result.quota.monthly_quota_cny_cents, false),
          }),
        );
        toast.success(t('customer_manager_360.toast.gift_over_quota_submitted'));
      }
      setIssueAmount('');
      setIssueReason('');
    } catch (err) {
      setFormError(errorText(err instanceof Error ? err : new Error(String(err))));
    }
  }

  async function submitRedeem(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (redeemCode.trim().length === 0) {
      setFormError(t('customer_manager_360.validation.gift_code'));
      return;
    }
    try {
      setFormError(null);
      await redeem.mutateAsync({
        orgId,
        body: {
          code: redeemCode.trim(),
          external_request_id: randomExternalId(),
          ...(redeemReason.trim().length > 0 ? { reason: redeemReason.trim() } : {}),
        },
      });
      setRedeemCode('');
      setRedeemReason('');
      toast.success(t('customer_manager_360.toast.gift_card_redeemed'));
    } catch (err) {
      setFormError(errorText(err instanceof Error ? err : new Error(String(err))));
    }
  }

  return (
    <div data-testid="customer-manager-360-gift-cards" className="grid gap-4 xl:grid-cols-2">
      {canWrite ? (
        <>
        <Panel caption={t('customer_manager_360.gift.issue')} padded>
          <div className="flex flex-col gap-3">
            <p className="m-0 text-dense text-paper-mid">{t('customer_manager_360.gift.issue_hint')}</p>
            <form onSubmit={(e) => void submitIssue(e)} className="flex flex-col gap-3">
              <TextField
                fieldId="customer-manager-360-gift-issue-amount"
                label={t('customer_manager_360.gift.amount_yuan')}
                value={issueAmount}
                onChange={setIssueAmount}
                type="number"
              />
              <TextField
                fieldId="customer-manager-360-gift-issue-reason"
                label={t('customer_manager_360.gift.reason')}
                value={issueReason}
                onChange={setIssueReason}
              />
              <Button type="submit" variant="primary" disabled={issue.isPending} className="justify-center">
                {t('customer_manager_360.action.issue')}
              </Button>
            </form>
            {issueResult !== null ? (
              <p
                className="m-0 text-body font-medium text-paper"
                data-testid="customer-manager-360-gift-issue-result"
              >
                {issueResult}
              </p>
            ) : null}
          </div>
        </Panel>
        <Panel caption={t('customer_manager_360.gift.redeem')} padded>
          <div className="flex flex-col gap-3">
            <KeyValue label={t('customer_manager_360.field.account_id')} value={detail.account.account_id} />
            <form onSubmit={(e) => void submitRedeem(e)} className="flex flex-col gap-3">
              <TextField
                fieldId="customer-manager-360-gift-redeem-code"
                label={t('customer_manager_360.gift.code')}
                value={redeemCode}
                onChange={setRedeemCode}
              />
              <TextField
                fieldId="customer-manager-360-gift-redeem-reason"
                label={t('customer_manager_360.gift.reason')}
                value={redeemReason}
                onChange={setRedeemReason}
              />
              <Button type="submit" variant="primary" disabled={redeem.isPending} className="justify-center">
                {t('customer_manager_360.action.redeem')}
              </Button>
            </form>
          </div>
        </Panel>
        {formError !== null ? (
          <div
            role="alert"
            className="rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3 text-body text-bad-bright xl:col-span-2"
          >
            {formError}
          </div>
        ) : null}
        </>
      ) : null}
      <div className="xl:col-span-2">
        <CreditLotsPanel orgId={orgId} cnyPerUsd={cnyPerUsd} />
      </div>
    </div>
  );
}

/**
 * Calendar days from now until `iso`. Both ends are floored to the local day
 * first, so a lot expiring at 08:00 tomorrow reads "in 1 day" rather than
 * "today" — the operator and the tenant both count calendar days, not elapsed
 * hours. The rounding after that is only DST slack: two floored midnights are
 * a whole number of days apart except across a clock shift.
 */
function daysUntil(iso: string, now: Date): number | null {
  const ms = parseTimestampMs(iso);
  if (!Number.isFinite(ms)) {
    return null;
  }
  const startOfDay = (d: Date): number =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((startOfDay(new Date(ms)) - startOfDay(now)) / 86_400_000);
}

/**
 * The lots still holding money on this account, in the order a charge will
 * consume them — the operator's side of the "含赠送 ¥X，Y 天后到期" line the
 * tenant sees on their own billing page.
 *
 * Read-only by design: lots are written by the gift and top-up paths and
 * retired by charges and the expiry sweep. Everything an operator can do to
 * them (issue, redeem) already has its own panel above.
 */
function CreditLotsPanel({
  orgId,
  cnyPerUsd,
}: {
  readonly orgId: string;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const lots = useCustomerManagerCreditLots(orgId, orgId.length > 0);
  const now = new Date();

  function expiryText(expiresAt: string | null): string {
    if (expiresAt === null) {
      return t('customer_manager_360.lots.never_expires');
    }
    const days = daysUntil(expiresAt, now);
    if (days === null) {
      return expiresAt;
    }
    if (days < 0) {
      return t('customer_manager_360.lots.expired');
    }
    const relative =
      days === 0
        ? t('customer_manager_360.lots.expires_today')
        : t('customer_manager_360.lots.expires_in_days', { days: String(days) });
    return `${formatLocalTimestamp(expiresAt)} · ${relative}`;
  }

  return (
    <Panel caption={t('customer_manager_360.lots.title')} padded>
      <div className="flex flex-col gap-3" data-testid="customer-manager-360-credit-lots">
        <p className="m-0 text-dense text-paper-mid">{t('customer_manager_360.lots.hint')}</p>
        {lots.isPending ? (
          <p className="m-0 text-dense text-paper-mid">{t('customer_manager_360.lots.loading')}</p>
        ) : null}
        {lots.isError ? (
          <p role="alert" className="m-0 text-dense text-bad-bright">
            {t('customer_manager_360.lots.error')}
          </p>
        ) : null}
        {lots.data !== undefined && lots.data.lots.length === 0 ? (
          <p className="m-0 text-dense text-paper-mid">{t('customer_manager_360.lots.empty')}</p>
        ) : null}
        {lots.data !== undefined && lots.data.lots.length > 0 ? (
          <>
            <Table>
              <THead>
                <Tr interactive={false}>
                  <Th>{t('customer_manager_360.lots.column.kind')}</Th>
                  <Th>{t('customer_manager_360.lots.column.source')}</Th>
                  <Th num>{t('customer_manager_360.lots.column.remaining')}</Th>
                  <Th num>{t('customer_manager_360.lots.column.amount')}</Th>
                  <Th>{t('customer_manager_360.lots.column.expires_at')}</Th>
                  <Th>{t('customer_manager_360.lots.column.owner')}</Th>
                </Tr>
              </THead>
              <TBody>
                {lots.data.lots.map((lot) => (
                  <Tr key={lot.id} interactive={false}>
                    <Td>
                      <StatusChip
                        variant={lot.kind === 'promo' ? 'warning' : 'neutral'}
                        label={
                          lot.kind === 'promo'
                            ? t('customer_manager_360.lots.kind.promo')
                            : t('customer_manager_360.lots.kind.cash')
                        }
                      />
                    </Td>
                    <Td>
                      <span className="font-mono text-dense text-paper-mid">
                        {lot.source_id ?? lot.source_type}
                      </span>
                    </Td>
                    <Td num>
                      {formatUsdMicrosOrCentsAsCnyDisplay(
                        lot.remaining_usd_micros,
                        lot.remaining_usd_cents,
                        cnyPerUsd, false)}
                    </Td>
                    <Td num>
                      {formatUsdMicrosOrCentsAsCnyDisplay(
                        lot.amount_usd_micros,
                        lot.amount_usd_cents,
                        cnyPerUsd, false)}
                    </Td>
                    <Td>{expiryText(lot.expires_at)}</Td>
                    {/* Lot carries only the ops user id; OpsUserRef resolves it against the ops-user directory client-side, falling back to the bare id. */}
                    <Td>
                      <OpsUserRef id={lot.owner_ops_user_id} />
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
            {lots.data.truncated ? (
              <p className="m-0 text-dense text-warn">
                {t('customer_manager_360.lots.truncated', {
                  count: String(lots.data.lots.length),
                })}
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    </Panel>
  );
}

/**
 * Scope-projected SKU rate row (#1104): the shared rate fields plus ONE
 * floor — procurement-derived for the full (lead/admin) view, the
 * lead-set sales floor for the sales (CM) view. The raw procurement
 * cost only exists on the full side; sales responses never carry it.
 */
interface SkuRateVm {
  readonly sku: OrgSkuRateCore;
  /** Scope-appropriate floor multiplier; null = no floor configured. */
  readonly floor: number | null;
  /** Procurement cost caption, full view only. */
  readonly upstreamCostCnyPerUsd: number | null;
}

interface SkuNamespaceGroupData {
  readonly namespaceSlug: string;
  readonly namespaceId: string;
  readonly vendor: string;
  readonly skus: readonly SkuRateVm[];
}

function toSkuRateVms(detail: CustomerManagerCustomer360Response): readonly SkuRateVm[] {
  if (detail.sku_rate_view === 'sales') {
    return detail.sku_rates.map((sku) => ({
      sku,
      floor: sku.sales_floor_multiplier,
      upstreamCostCnyPerUsd: null,
    }));
  }
  return detail.sku_rates.map((sku) => ({
    sku,
    floor: sku.minimum_rate_multiplier,
    upstreamCostCnyPerUsd: sku.upstream_cost_cny_per_usd,
  }));
}

/**
 * The line rows, keyed by namespace id (2026-07-31 design §5.2).
 *
 * `namespace_rates` is OPTIONAL on the wire so a platform one deploy behind
 * degrades to "no line rows" rather than failing the parse that gates the whole
 * Customer 360. An absent map therefore renders the per-model table exactly as
 * before, which is the intended degradation and not an error state.
 */
function toNamespaceRateVms(
  detail: CustomerManagerCustomer360Response,
): ReadonlyMap<string, NamespaceRateVm> {
  const out = new Map<string, NamespaceRateVm>();
  if (detail.sku_rate_view === 'sales') {
    for (const namespace of detail.account.org?.namespace_rates ?? []) {
      out.set(namespace.namespace_id, { namespace, floor: namespace.sales_floor_multiplier });
    }
    return out;
  }
  for (const namespace of detail.account.org?.namespace_rates ?? []) {
    out.set(namespace.namespace_id, { namespace, floor: namespace.minimum_rate_multiplier });
  }
  return out;
}

function OrgModelPlanBanner({
  orgId,
  planName,
  planId,
  canRebindPlan,
  canManagePlan,
}: {
  readonly orgId: string;
  readonly planName: string;
  readonly planId: string | null;
  readonly canRebindPlan: boolean;
  readonly canManagePlan: boolean;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const clone = useCloneModelPlan();
  const rebind = useUpdateOrgModelPlan();
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneName, setCloneName] = useState(`${planName} · ${orgId}`);
  const [detachOpen, setDetachOpen] = useState(false);

  return (
    <div
      className="mx-4 mt-4 flex flex-col gap-3 rounded-lg border border-signal-deep/50 bg-signal-dim px-4 py-3"
      data-testid="customer-360-model-plan-banner"
    >
      <p className="m-0 text-body text-paper">
        {t('customer_manager_360.model_plan.banner', { name: planName })}
      </p>
      <p className="m-0 text-dense text-paper-mid">
        {t('customer_manager_360.model_plan.banner_hint')}
      </p>
      <div className="flex flex-wrap gap-2">
        <Link
          to="/market/model-plans"
          className={buttonClass('secondary', 'sm')}
          data-testid="customer-360-model-plan-edit"
        >
          {t('customer_manager_360.model_plan.edit_plan')}
        </Link>
        {canManagePlan && planId !== null ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setCloneOpen(true)}
            data-testid="customer-360-model-plan-clone-rebind"
          >
            {t('customer_manager_360.model_plan.clone_rebind')}
          </Button>
        ) : null}
        {canRebindPlan ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDetachOpen(true)}
            data-testid="customer-360-model-plan-detach"
          >
            {t('customer_manager_360.model_plan.detach')}
          </Button>
        ) : null}
      </div>

      {cloneOpen ? (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next && !clone.isPending && !rebind.isPending) setCloneOpen(false);
          }}
          title={t('customer_manager_360.model_plan.clone_rebind')}
          closeLabel={t('common.close')}
          testId="customer-360-model-plan-clone-dialog"
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => setCloneOpen(false)}
                disabled={clone.isPending || rebind.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                loading={clone.isPending || rebind.isPending}
                onClick={() => {
                  const trimmed = cloneName.trim();
                  if (trimmed.length === 0 || planId === null) return;
                  clone.mutate(
                    { id: planId, body: { name: trimmed } },
                    {
                      onSuccess: (created) => {
                        rebind.mutate(
                          {
                            orgId,
                            body: { model_plan_id: created.model_plan.id },
                          },
                          {
                            onSuccess: () => {
                              setCloneOpen(false);
                              toast.success(t('customer_manager_360.model_plan.toast_assigned'));
                            },
                            onError: (err) => {
                              toast.error(coerceErrorMessage(err));
                            },
                          },
                        );
                      },
                      onError: (err) => {
                        toast.error(coerceErrorMessage(err));
                      },
                    },
                  );
                }}
                data-testid="customer-360-model-plan-clone-submit"
              >
                {t('model_plans.clone.submit')}
              </Button>
            </>
          }
        >
          <FormField
            label={t('customer_manager_360.model_plan.clone_name')}
            fieldId="customer-360-clone-name"
          >
            <Input
              id="customer-360-clone-name"
              value={cloneName}
              onChange={(event) => setCloneName(event.currentTarget.value)}
              data-testid="customer-360-clone-name"
            />
          </FormField>
        </Dialog>
      ) : null}

      <ConfirmDialog
        open={detachOpen}
        onOpenChange={(open) => {
          if (!open && !rebind.isPending) setDetachOpen(false);
        }}
        title={t('customer_manager_360.model_plan.detach_title')}
        body={t('customer_manager_360.model_plan.detach_body')}
        confirmLabel={t('customer_manager_360.model_plan.detach_confirm')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        tone="danger"
        loading={rebind.isPending}
        onConfirm={() => {
          rebind.mutate(
            { orgId, body: { model_plan_id: null } },
            {
              onSuccess: () => {
                setDetachOpen(false);
                toast.success(t('customer_manager_360.model_plan.toast_detached'));
              },
              onError: (err) => {
                toast.error(coerceErrorMessage(err));
              },
            },
          );
        }}
        testId="customer-360-model-plan-detach-confirm"
      />
    </div>
  );
}

function OrgModelPlanAssignControl({ orgId }: { readonly orgId: string }): JSX.Element {
  const t = useT();
  const toast = useToast();
  const assign = useUpdateOrgModelPlan();
  const [open, setOpen] = useState(false);
  const [planId, setPlanId] = useState<string | null>(null);

  return (
    <div className="mx-4 mt-4 flex flex-wrap items-center gap-2">
      <Button
        size="sm"
        variant="secondary"
        onClick={() => setOpen(true)}
        data-testid="customer-360-model-plan-assign"
      >
        {t('customer_manager_360.model_plan.assign')}
      </Button>
      {open ? (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next && !assign.isPending) setOpen(false);
          }}
          title={t('customer_manager_360.model_plan.assign_title')}
          closeLabel={t('common.close')}
          testId="customer-360-model-plan-assign-dialog"
          footer={
            <>
              <Button variant="ghost" onClick={() => setOpen(false)} disabled={assign.isPending}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                disabled={planId === null}
                loading={assign.isPending}
                onClick={() => {
                  if (planId === null) return;
                  assign.mutate(
                    { orgId, body: { model_plan_id: planId } },
                    {
                      onSuccess: () => {
                        setOpen(false);
                        toast.success(t('customer_manager_360.model_plan.toast_assigned'));
                      },
                      onError: (err) => {
                        toast.error(coerceErrorMessage(err));
                      },
                    },
                  );
                }}
                data-testid="customer-360-model-plan-assign-submit"
              >
                {t('customer_manager_360.model_plan.assign_submit')}
              </Button>
            </>
          }
        >
          <ModelPlanSelect
            brand={undefined}
            value={planId}
            onChange={setPlanId}
            allowNone={false}
            fieldId="customer-360-assign-plan"
            label={t('invite_codes.rebind.plan_label')}
            testId="customer-360-assign-plan"
          />
        </Dialog>
      ) : null}
    </div>
  );
}

function SkuRatesTab({
  rates,
  namespaceRates,
  view,
  orgId,
  canWrite,
  lockedByPlan,
  hostedPlanName,
  hostedPlanId,
  canRebindPlan,
  canManagePlan,
  onManagedLock,
  onRefresh,
}: {
  readonly rates: readonly SkuRateVm[];
  readonly namespaceRates: ReadonlyMap<string, NamespaceRateVm>;
  readonly view: OrgSkuRateView;
  readonly orgId: string;
  /** #1958: rate + visibility edits are writes; the resolved numbers are reads. */
  readonly canWrite: boolean;
  readonly lockedByPlan: boolean;
  readonly hostedPlanName: string | null;
  readonly hostedPlanId: string | null;
  readonly canRebindPlan: boolean;
  readonly canManagePlan: boolean;
  readonly onManagedLock: (name: string) => void;
  /** Resolves only after a verified successful authoritative detail read. */
  readonly onRefresh: () => Promise<void>;
}): JSX.Element {
  const t = useT();
  const groups = useMemo(() => groupSkusByNamespace(rates), [rates]);
  // Requester-side pending discount_below_sales_cost chips (#1106) —
  // sales view only; lead/admin writes never convert into approvals.
  const pendingDiscounts = useMyPendingDiscountRequests(view === 'sales');
  const pendingDiscountTargets = useMemo(
    () => pendingDiscountsByTargetId(pendingDiscounts.data),
    [pendingDiscounts.data],
  );
  return (
    <Panel
      testId="customer-manager-360-sku-rates"
      caption={t('customer_manager_360.sku_rates.title')}
    >
      {hostedPlanName !== null ? (
        <OrgModelPlanBanner
          orgId={orgId}
          planName={hostedPlanName}
          planId={hostedPlanId}
          canRebindPlan={canRebindPlan}
          canManagePlan={canManagePlan}
        />
      ) : canRebindPlan ? (
        <OrgModelPlanAssignControl orgId={orgId} />
      ) : null}
      {rates.length === 0 ? (
        <p className="m-0 p-4 text-body text-paper-low">{t('customer_manager_360.empty.sku_rates')}</p>
      ) : (
        <div className="flex flex-col gap-4 p-4">
          {groups.map((group) => (
            <SkuNamespaceGroup
              key={group.namespaceSlug}
              group={group}
              line={namespaceRates.get(group.namespaceId) ?? null}
              view={view}
              orgId={orgId}
              canWrite={canWrite}
              lockedByPlan={lockedByPlan}
              onManagedLock={onManagedLock}
              pendingDiscountTargets={pendingDiscountTargets}
              onRefresh={onRefresh}
            />
          ))}
        </div>
      )}
    </Panel>
  );
}

/** Per-SKU write outcome for row + bulk tallies (#1106). */
type SkuRateWriteOutcome = 'applied' | 'pending' | 'failed';

/**
 * A staged SKU (#1156) needs a customer-specific rate before it can be
 * opened. Historical/default-visible SKUs keep their existing inheritance
 * behavior, so an absent override is only blocking when the server says this
 * SKU defaults hidden for the organisation.
 */
function skuNeedsActivationRate(row: SkuRateVm): boolean {
  return row.sku.org_visibility_default === false && row.sku.override_rate_multiplier === null;
}

type SkuVisibilityConvergenceKind = 'pending' | 'degraded' | 'unknown';

interface SkuVisibilityConvergenceState {
  readonly kind: SkuVisibilityConvergenceKind;
  /** null means the wire response had no valid authoritative deadline. */
  readonly deadline: number | null;
  readonly revision: string | null;
  /** Local generation prevents an older in-flight refresh clearing new state. */
  readonly generation: number;
  /** Failed/unknown states require a bounded manual retry, never a zero-delay loop. */
  readonly refreshBlocked: boolean;
  readonly refreshing: boolean;
}

function SkuNamespaceGroup({
  group,
  line,
  view,
  orgId,
  canWrite,
  lockedByPlan,
  onManagedLock,
  pendingDiscountTargets,
  onRefresh,
}: {
  readonly group: SkuNamespaceGroupData;
  /** The line agreement, or null when the platform sent no row for it. */
  readonly line: NamespaceRateVm | null;
  readonly view: OrgSkuRateView;
  readonly orgId: string;
  /** #1958: false hides the bulk + line + per-row editors, keeps the table. */
  readonly canWrite: boolean;
  readonly lockedByPlan: boolean;
  readonly onManagedLock: (name: string) => void;
  readonly pendingDiscountTargets: ReadonlyMap<string, unknown>;
  /** Resolves only after a verified successful authoritative detail read. */
  readonly onRefresh: () => Promise<void>;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const rateMut = useUpdateCustomerManagerSkuRate();
  const visibilityMut = useUpdateCustomerManagerSkuVisibility();
  const [rowErrors, setRowErrors] = useState<Record<string, string | null>>({});
  const [bulkRate, setBulkRate] = useState('');
  const [running, setRunning] = useState(false);
  const [bulkSummary, setBulkSummary] = useState<{ message: string; failed: boolean } | null>(null);
  const bulkSummaryRef = useRef<HTMLParagraphElement>(null);
  const [bulkShowPreview, setBulkShowPreview] = useState(false);
  const convergenceGeneration = useRef(0);
  const [convergenceStates, setConvergenceStates] = useState<
    ReadonlyMap<string, SkuVisibilityConvergenceState>
  >(new Map());
  const busy = running || rateMut.isPending || visibilityMut.isPending;
  const editorsEnabled = canWrite && !lockedByPlan;
  const lockHint = t('customer_manager_360.model_plan.lock_tooltip');
  const hiddenRows = group.skus.filter((row) => !row.sku.visible);
  const blockedShowRows = hiddenRows.filter(skuNeedsActivationRate);
  const readyShowRows = hiddenRows.filter((row) => !skuNeedsActivationRate(row));
  const bulkShowReasonId = `customer-sku-bulk-show-reason-${group.namespaceSlug}`;

  useEffect(() => {
    if (bulkSummary?.failed) {
      bulkSummaryRef.current?.focus();
    }
  }, [bulkSummary]);

  useEffect(() => {
    const scheduled = [...convergenceStates.entries()].filter(
      (entry): entry is [string, SkuVisibilityConvergenceState & { deadline: number }] =>
        entry[1].deadline !== null && !entry[1].refreshBlocked && !entry[1].refreshing,
    );
    if (scheduled.length === 0) return;
    const nextDeadline = Math.min(...scheduled.map(([, state]) => state.deadline));
    const timer = window.setTimeout(() => {
      const firedAt = Date.now();
      const due = scheduled
        .filter(([, state]) => state.deadline <= firedAt)
        .map(([skuId, state]) => ({ skuId, generation: state.generation }));
      if (due.length === 0) return;

      setConvergenceStates((previous) => {
        const next = new Map(previous);
        for (const snapshot of due) {
          const current = next.get(snapshot.skuId);
          if (current?.generation === snapshot.generation) {
            next.set(snapshot.skuId, { ...current, refreshing: true });
          }
        }
        return next;
      });

      void (async () => {
        try {
          await onRefresh();
          setConvergenceStates((previous) => {
            const next = new Map(previous);
            for (const snapshot of due) {
              const current = next.get(snapshot.skuId);
              if (
                current?.generation === snapshot.generation &&
                current.deadline !== null &&
                current.deadline <= firedAt
              ) {
                next.delete(snapshot.skuId);
              }
            }
            return next;
          });
        } catch {
          setConvergenceStates((previous) => {
            const next = new Map(previous);
            for (const snapshot of due) {
              const current = next.get(snapshot.skuId);
              if (current?.generation === snapshot.generation) {
                next.set(snapshot.skuId, {
                  ...current,
                  refreshBlocked: true,
                  refreshing: false,
                });
              }
            }
            return next;
          });
        }
      })();
    }, Math.max(0, nextDeadline - Date.now()));
    return () => window.clearTimeout(timer);
  }, [convergenceStates, onRefresh]);

  function setRowError(skuId: string, message: string | null): void {
    setRowErrors((prev) => ({ ...prev, [skuId]: message }));
  }

  // Per-SKU rate write (#1104/#1106). The FULL (lead/admin) view keeps
  // the client-side floor block — its floor is the procurement hard
  // floor and a below-floor write would only 400 server-side. The SALES
  // view submits below-floor rates: the server converts them into a
  // pending discount_below_sales_cost approval instead of applying
  // (`outcome: 'approval_pending'`), which is the #1106 request-approval
  // path. Failures land on the SKU's own row via `rowErrors`.
  async function applyRate(
    row: SkuRateVm,
    nextRate: number | null,
    note: string | null,
  ): Promise<SkuRateWriteOutcome> {
    if (
      view !== 'sales' &&
      nextRate !== null &&
      row.floor !== null &&
      nextRate < row.floor
    ) {
      setRowError(
        row.sku.sku_id,
        t('customer_manager_360.validation.rate_floor', { floor: fmtRate(row.floor) }),
      );
      return 'failed';
    }
    try {
      const result = await rateMut.mutateAsync({
        orgId,
        body: { sku_id: row.sku.sku_id, rate_multiplier: nextRate, note },
      });
      setRowError(row.sku.sku_id, null);
      return result.outcome === 'approval_pending' ? 'pending' : 'applied';
    } catch (err) {
      const normalized = err instanceof Error ? err : new Error(String(err));
      // Two refusals this row can provoke get operator copy rather than the
      // raw code. The line grain renders the same floor refusal as a table of
      // breaching models, which is what a wide line needs; here the breach is
      // always this one row, whose name the operator is already looking at, so
      // a sentence carries the same information without a one-row table.
      const managed = modelPlanManagedFromError(normalized);
      if (managed !== null) {
        onManagedLock(managed.model_plan_name);
        toast.error(
          t('customer_manager_360.model_plan.managed_toast', { name: managed.model_plan_name }),
        );
        return 'failed';
      }
      setRowError(
        row.sku.sku_id,
        normalized instanceof OpsApiError && normalized.code === 'SKU_RATE_VISIBLE_CANNOT_CLEAR'
          ? t('customer_manager_360.visibility.clear_requires_hidden')
          : normalized instanceof OpsApiError && normalized.code === 'NAMESPACE_RATE_BELOW_SKU_FLOOR'
            ? t('customer_manager_360.rate.clear_below_line_floor')
            : errorText(normalized),
      );
      return 'failed';
    }
  }

  async function applyVisibility(row: SkuRateVm, nextVisible: boolean): Promise<boolean> {
    if (nextVisible && !row.sku.visible && skuNeedsActivationRate(row)) {
      setRowError(
        row.sku.sku_id,
        pendingDiscountTargets.has(`${orgId}:${row.sku.sku_id}`)
          ? t('customer_manager_360.visibility.show_waits_for_approval')
          : t('customer_manager_360.visibility.show_requires_rate'),
      );
      return false;
    }
    try {
      const result = await visibilityMut.mutateAsync({
        orgId,
        skuId: row.sku.sku_id,
        body: { visible: nextVisible },
      });
      const wire = result as {
        readonly convergence?: unknown;
        readonly effective_after?: unknown;
        readonly revision?: unknown;
      };
      const generation = ++convergenceGeneration.current;
      setConvergenceStates((previous) => {
        const next = new Map(previous);
        if (wire.convergence === 'immediate') {
          next.delete(row.sku.sku_id);
          return next;
        }

        const parsedDeadline =
          typeof wire.effective_after === 'string'
            ? Date.parse(wire.effective_after)
            : Number.NaN;
        const revision =
          typeof wire.revision === 'string' && wire.revision.length > 0
            ? wire.revision
            : null;
        if (
          (wire.convergence === 'pending' || wire.convergence === 'degraded') &&
          Number.isFinite(parsedDeadline)
        ) {
          next.set(row.sku.sku_id, {
            kind: wire.convergence,
            deadline: parsedDeadline,
            revision,
            generation,
            refreshBlocked: false,
            refreshing: false,
          });
          return next;
        }

        // Missing/invalid deadlines and unknown future values are not treated
        // as immediate. Keep an explicit unknown state until a manual,
        // authoritative refresh succeeds.
        next.set(row.sku.sku_id, {
          kind: 'unknown',
          deadline: null,
          revision,
          generation,
          refreshBlocked: true,
          refreshing: false,
        });
        return next;
      });
      setRowError(row.sku.sku_id, null);
      return true;
    } catch (err) {
      const normalized = err instanceof Error ? err : new Error(String(err));
      const managed = modelPlanManagedFromError(normalized);
      if (managed !== null) {
        onManagedLock(managed.model_plan_name);
        toast.error(
          t('customer_manager_360.model_plan.managed_toast', { name: managed.model_plan_name }),
        );
        return false;
      }
      setRowError(
        row.sku.sku_id,
        normalized instanceof OpsApiError && normalized.code === 'SKU_RATE_NOT_CONFIGURED'
          ? t('customer_manager_360.visibility.show_requires_rate')
          : errorText(normalized),
      );
      return false;
    }
  }

  async function retryConvergence(skuId: string): Promise<void> {
    const snapshot = convergenceStates.get(skuId);
    if (snapshot === undefined || snapshot.refreshing) return;
    setConvergenceStates((previous) => {
      const current = previous.get(skuId);
      if (current?.generation !== snapshot.generation) return previous;
      const next = new Map(previous);
      next.set(skuId, { ...current, refreshing: true });
      return next;
    });
    try {
      await onRefresh();
      setConvergenceStates((previous) => {
        const current = previous.get(skuId);
        if (current?.generation !== snapshot.generation) return previous;
        const next = new Map(previous);
        next.delete(skuId);
        return next;
      });
    } catch {
      setConvergenceStates((previous) => {
        const current = previous.get(skuId);
        if (current?.generation !== snapshot.generation) return previous;
        const next = new Map(previous);
        next.set(skuId, {
          ...current,
          refreshBlocked: true,
          refreshing: false,
        });
        return next;
      });
    }
  }

  async function handleRowSave(
    row: SkuRateVm,
    nextRate: number | null,
    note: string | null,
  ): Promise<void> {
    const outcome = await applyRate(row, nextRate, note);
    if (outcome === 'applied') {
      toast.success(
        nextRate !== null && row.sku.org_visibility_default === false && !row.sku.visible
          ? t('customer_manager_360.toast.sku_rate_saved_still_hidden')
          : t('customer_manager_360.toast.sku_rate_updated'),
      );
    } else if (outcome === 'pending') {
      toast.success(t('customer_manager_360.toast.discount_approval_submitted'));
    }
  }

  async function handleRowVisibility(row: SkuRateVm): Promise<void> {
    if (await applyVisibility(row, !row.sku.visible)) {
      toast.success(t('customer_manager_360.toast.sku_visibility_updated'));
    }
  }

  function reportBulk(applied: number, pending: number, failed: number): void {
    setBulkSummary({
      message: t('customer_manager_360.visibility.bulk_result', { applied, pending, failed }),
      failed: failed > 0,
    });
    if (failed === 0) {
      const message = t('customer_manager_360.toast.bulk_applied', { count: applied });
      toast.success(message);
    } else {
      const message = t('customer_manager_360.toast.bulk_partial', { ok: applied, failed });
      toast.error(message);
    }
    if (pending > 0) {
      toast.success(t('customer_manager_360.toast.bulk_discount_pending', { count: pending }));
    }
  }

  async function handleBulkRate(): Promise<void> {
    const parsed = Number(bulkRate);
    if (bulkRate.trim().length === 0 || !Number.isFinite(parsed)) return;
    setRunning(true);
    let applied = 0;
    let pending = 0;
    let failed = 0;
    for (const row of group.skus) {
      const outcome = await applyRate(row, parsed, row.sku.note);
      if (outcome === 'applied') applied += 1;
      else if (outcome === 'pending') pending += 1;
      else failed += 1;
    }
    setRunning(false);
    reportBulk(applied, pending, failed);
  }

  async function handleBulkVisibility(nextVisible: boolean): Promise<void> {
    setRunning(true);
    let applied = 0;
    let failed = 0;
    for (const row of group.skus) {
      // Idempotent rows are not mutations and must not inflate the success
      // count. This also avoids needless visibility PUTs and audit records.
      if (row.sku.visible === nextVisible) continue;
      if (await applyVisibility(row, nextVisible)) applied += 1;
      else failed += 1;
    }
    setRunning(false);
    setBulkShowPreview(false);
    reportBulk(applied, 0, failed);
  }

  return (
    <div
      data-testid={`customer-manager-360-sku-namespace-${group.namespaceSlug}`}
      className="flex flex-col gap-3 rounded-lg border border-line-soft bg-well px-3 py-3"
    >
      <NamespaceRateGroup
        line={line}
        orgId={orgId}
        salesView={view === 'sales'}
        skuCount={group.skus.length}
        vendor={group.vendor}
        namespaceSlug={group.namespaceSlug}
        canWrite={canWrite}
        lockedByPlan={lockedByPlan}
        onManagedLock={(name) => {
          onManagedLock(name);
          toast.error(t('customer_manager_360.model_plan.managed_toast', { name }));
        }}
      />
      {canWrite ? (
        <PlanLockWrap locked={lockedByPlan} hint={lockHint}>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex items-end gap-1.5">
                <Input
                  mono
                  value={bulkRate}
                  onChange={(e) => setBulkRate(e.currentTarget.value)}
                  type="number"
                  step="0.0001"
                  aria-label={t('customer_manager_360.sku_rates.bulk_rate_label')}
                  placeholder={t('customer_manager_360.sku_rates.bulk_rate_label')}
                  className="w-28"
                  disabled={!editorsEnabled}
                  data-testid={`customer-sku-bulk-rate-input-${group.namespaceSlug}`}
                />
                <Button
                  size="sm"
                  disabled={busy || bulkRate.trim().length === 0 || !editorsEnabled}
                  onClick={() => void handleBulkRate()}
                  data-testid={`customer-sku-bulk-rate-apply-${group.namespaceSlug}`}
                >
                  {t('customer_manager_360.sku_rates.bulk_apply')}
                </Button>
              </div>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy || !editorsEnabled}
                onClick={() => void handleBulkVisibility(false)}
                data-testid={`customer-sku-bulk-hide-${group.namespaceSlug}`}
              >
                <EyeOff aria-hidden="true" className="h-3.5 w-3.5" />
                {t('customer_manager_360.action.hide_namespace')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy || readyShowRows.length === 0 || !editorsEnabled}
                aria-describedby={blockedShowRows.length > 0 ? bulkShowReasonId : undefined}
                onClick={() => setBulkShowPreview(true)}
                data-testid={`customer-sku-bulk-show-${group.namespaceSlug}`}
              >
                <Eye aria-hidden="true" className="h-3.5 w-3.5" />
                {t('customer_manager_360.action.show_namespace')}
              </Button>
            </div>
          </div>
        </PlanLockWrap>
      ) : null}
      {canWrite && blockedShowRows.length > 0 ? (
        <p id={bulkShowReasonId} role="status" className="m-0 text-label text-warn">
          {readyShowRows.length === 0
            ? t('customer_manager_360.visibility.bulk_show_blocked')
            : t('customer_manager_360.visibility.bulk_show_partial', {
                count: blockedShowRows.length,
              })}
        </p>
      ) : null}
      {bulkSummary !== null ? (
        <p
          ref={bulkSummaryRef}
          tabIndex={-1}
          role={bulkSummary.failed ? 'alert' : 'status'}
          data-testid={`customer-sku-bulk-summary-${group.namespaceSlug}`}
          className={bulkSummary.failed ? 'm-0 text-label text-bad' : 'm-0 text-label text-ok'}
        >
          {bulkSummary.message}
        </p>
      ) : null}
      {bulkShowPreview ? (
        <div
          role="region"
          aria-label={t('customer_manager_360.sku_rates.bulk_preview_title')}
          className="rounded-lg border border-warn-line bg-warn-dim/40 p-3"
          data-testid={`customer-sku-bulk-show-preview-${group.namespaceSlug}`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-section text-paper">{t('customer_manager_360.sku_rates.bulk_preview_title')}</span>
              <span className="text-label text-paper-low">{t('customer_manager_360.sku_rates.bulk_preview_hint')}</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setBulkShowPreview(false)}>
                {t('common.cancel')}
              </Button>
              <Button size="sm" disabled={busy} onClick={() => void handleBulkVisibility(true)}>
                {t('customer_manager_360.sku_rates.bulk_confirm_show')}
              </Button>
            </div>
          </div>
          <ul className="m-0 mt-3 grid list-none gap-2 p-0 md:grid-cols-2">
            {group.skus.map((row) => {
              const pending = pendingDiscountTargets.has(`${orgId}:${row.sku.sku_id}`);
              const priced = row.sku.override_rate_multiplier !== null;
              const readiness = pending ? 'pending_discount' : priced ? 'ready' : 'unpriced';
              return (
                <li key={row.sku.sku_id} className="rounded bg-card px-3 py-2 text-dense">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-paper">{row.sku.sku_name}</span>
                    <StatusChip
                      variant={readiness === 'ready' ? 'ok' : 'warning'}
                      label={
                        readiness === 'ready'
                          ? t('customer_manager_360.sku_rates.bulk_ready')
                          : readiness === 'pending_discount'
                            ? t('customer_manager_360.sku_rates.bulk_blocked_pending_discount')
                            : t('customer_manager_360.sku_rates.bulk_blocked_unpriced')
                      }
                    />
                  </div>
                  <div className="mt-1 text-label text-paper-low">
                    {readiness === 'ready'
                      ? t('customer_manager_360.sku_rates.bulk_preview_row_ready', {
                          price: fmtRate(row.sku.override_rate_multiplier),
                        })
                      : readiness === 'pending_discount'
                        ? t('customer_manager_360.sku_rates.bulk_preview_row_pending_discount')
                        : t('customer_manager_360.sku_rates.bulk_preview_row_unpriced')}
                  </div>
                </li>
              );
            })}
          </ul>
         </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-dense">
          <THead>
            <Tr interactive={false}>
              <Th>{t('customer_manager_360.column.model')}</Th>
              <Th>{t('customer_manager_360.column.visibility')}</Th>
              <Th num>{t('customer_manager_360.column.platform_rate')}</Th>
              <Th num>
                {view === 'sales'
                  ? t('customer_manager_360.column.sales_floor')
                  : t('customer_manager_360.column.cost_floor')}
              </Th>
              <Th num>{t('customer_manager_360.column.effective_rate')}</Th>
              <Th>{t('customer_manager_360.column.note')}</Th>
              <Th>{t('customer_manager_360.column.action')}</Th>
            </Tr>
          </THead>
          <TBody>
            {group.skus.map((row) => (
              <SkuRateRow
                key={row.sku.sku_id}
                row={row}
                isPending={busy}
                canWrite={canWrite}
                lockedByPlan={lockedByPlan}
                salesView={view === 'sales'}
                pendingDiscount={pendingDiscountTargets.has(`${orgId}:${row.sku.sku_id}`)}
                convergenceState={convergenceStates.get(row.sku.sku_id) ?? null}
                error={rowErrors[row.sku.sku_id] ?? null}
                onSave={handleRowSave}
                onToggleVisibility={handleRowVisibility}
                onRetryConvergence={retryConvergence}
              />
            ))}
          </TBody>
        </table>
      </div>
    </div>
  );
}

function SkuRateRow({
  row,
  isPending,
  canWrite,
  lockedByPlan,
  salesView,
  pendingDiscount,
  convergenceState,
  error,
  onSave,
  onToggleVisibility,
  onRetryConvergence,
}: {
  readonly row: SkuRateVm;
  readonly isPending: boolean;
  /** #1958: false renders the resolved state without the visibility/rate editors. */
  readonly canWrite: boolean;
  readonly lockedByPlan: boolean;
  /** Sales view (#1106): below-floor input is allowed and converts into an approval. */
  readonly salesView: boolean;
  /** An open discount_below_sales_cost request exists for this org × SKU. */
  readonly pendingDiscount: boolean;
  readonly convergenceState: SkuVisibilityConvergenceState | null;
  readonly error: string | null;
  readonly onSave: (row: SkuRateVm, nextRate: number | null, note: string | null) => Promise<void>;
  readonly onToggleVisibility: (row: SkuRateVm) => Promise<void>;
  readonly onRetryConvergence: (skuId: string) => Promise<void>;
}): JSX.Element {
  const t = useT();
  const sku = row.sku;
  // ── the two grains, and why both predicates exist ────────────────────────
  //
  // `override_rate_multiplier` is non-null when the winning rate came from
  // EITHER grain (the wire value is `CASE WHEN rate_source IN (sku_override,
  // namespace_override) THEN rate_multiplier END`). So it answers "is there an
  // agreed rate", and it CANNOT answer "does this model hold its own row".
  //
  // Three of this row's controls ask the first question and are right to use it:
  // the rate-applied chip, the activation state, and the open-button gate, which
  // mirrors the server guard (`AGREED_RATE_SOURCES.includes(rate_source)`).
  // Narrowing them to the SKU grain would disable opening a model whose line
  // agreement the server accepts — the console refusing what the platform
  // permits, on exactly the rows the namespace collapse creates.
  //
  // Clear and the edit box ask the SECOND question, because a per-model clear
  // issues a DELETE keyed on (org_id, sku_id). Fired for an inheriting model it
  // matches nothing, raises no error, and reports success while the customer
  // keeps the rate.
  const hasAppliedRate = sku.override_rate_multiplier !== null;
  // `rate_source` is optional on the wire so a platform one deploy behind
  // degrades to a missing badge rather than an unusable page. When it is absent
  // we cannot distinguish the grains, so fall back to the old behavior: a
  // best-effort clear beats disabling a money control over a missing field.
  const rateSource = sku.rate_source;
  const ownsSkuRate = rateSource === undefined ? hasAppliedRate : rateSource === 'sku_override';
  const inheritsLineRate = rateSource === 'namespace_override';
  const [value, setValue] = useState(
    // Prefill from the model's OWN rate only. Seeding the box with an inherited
    // line rate meant that saving wrote a SKU-grain row carrying the line's
    // number — silently converting an inherited rate into a permanent per-model
    // exception, with identical numbers on screen before and after.
    ownsSkuRate ? String(sku.override_rate_multiplier ?? sku.rate_multiplier) : '',
  );
  const [note, setNote] = useState(sku.note ?? '');
  const visible = sku.visible;
  const activationRateMissing = skuNeedsActivationRate(row);
  const showBlocked = !visible && activationRateMissing;
  const clearBlocked =
    !ownsSkuRate || (visible && sku.org_visibility_default === false && hasAppliedRate);
  const showReasonId = `customer-sku-show-reason-${sku.sku_id}`;
  const clearReasonId = `customer-sku-clear-reason-${sku.sku_id}`;
  const [confirmShow, setConfirmShow] = useState(false);
  const activationState: 'open' | 'discount_pending' | 'priced' | 'unpriced' = visible
    ? 'open'
    : pendingDiscount
      ? 'discount_pending'
      : hasAppliedRate
        ? 'priced'
        : 'unpriced';
  const openBlockReasonKey: I18nKey | null = visible
    ? null
    : pendingDiscount
      ? 'customer_manager_360.sku_rates.open_blocked_pending_discount'
      : !activationRateMissing
        ? null
        : 'customer_manager_360.sku_rates.open_blocked_unpriced';
  function parsedRate(): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const enteredRate = parsedRate();
  const belowSalesFloor =
    salesView && enteredRate !== null && row.floor !== null && enteredRate < row.floor;
  const editorsEnabled = canWrite && !lockedByPlan;
  const lockHint = t('customer_manager_360.model_plan.lock_tooltip');
  return (
    <Tr>
      <Td>
        <div className="flex flex-col items-start gap-1">
          <span className="font-medium text-paper">{sku.model}</span>
          <span className="font-mono text-micro text-paper-low">{sku.sku_name}</span>
          {/*
            Which layer priced this model. Rendered only when the platform sent
            it: an absent badge is the intended degradation, and inventing one
            would state a grain we do not know.
          */}
          {rateSource === undefined ? null : (
            <StatusChip
              variant={ownsSkuRate ? 'ok' : inheritsLineRate ? 'neutral' : 'warning'}
              testId={`customer-sku-rate-source-${sku.sku_id}`}
              label={t(
                ownsSkuRate
                  ? 'customer_manager_360.rate_source.sku_override'
                  : inheritsLineRate
                    ? 'customer_manager_360.rate_source.inherited'
                    : 'customer_manager_360.rate_source.platform',
              )}
            />
          )}
          {sku.namespace_rate_applies === false ? (
            <Tooltip content={t('customer_manager_360.rate_source.optout_hint')}>
              <span className="inline-flex">
                <StatusChip
                  variant="warning"
                  testId={`customer-sku-namespace-optout-${sku.sku_id}`}
                  label={t('customer_manager_360.rate_source.optout')}
                />
              </span>
            </Tooltip>
          ) : null}
        </div>
      </Td>
      <Td>
        <div className="flex min-w-40 flex-col items-start gap-2">
          <div className="flex items-center gap-2">
            <StatusChip
              variant={
                convergenceState?.kind === 'unknown'
                  ? 'error'
                  : convergenceState !== null
                    ? 'warning'
                    : activationState === 'open'
                      ? 'ok'
                      : 'warning'
              }
              label={
                convergenceState?.kind === 'pending'
                  ? t('customer_manager_360.sku_rates.state_converging')
                  : convergenceState?.kind === 'degraded'
                    ? t('customer_manager_360.sku_rates.state_degraded')
                    : convergenceState?.kind === 'unknown'
                      ? t('customer_manager_360.sku_rates.state_unknown')
                      : activationState === 'open'
                        ? t('customer_manager_360.sku_rates.state_open')
                        : activationState === 'discount_pending'
                          ? t('customer_manager_360.sku_rates.state_discount_pending')
                          : activationState === 'priced'
                            ? t('customer_manager_360.sku_rates.state_priced')
                            : t('customer_manager_360.sku_rates.state_unpriced')
              }
            />
            {canWrite ? (
              <PlanLockWrap locked={lockedByPlan} hint={lockHint}>
              <Button
                size="sm"
                variant="ghost"
                disabled={isPending || showBlocked || !editorsEnabled}
                aria-label={
                  visible
                    ? t('customer_manager_360.action.hide_sku_for', { sku: sku.sku_name })
                    : confirmShow
                      ? t('customer_manager_360.action.confirm_show_sku')
                      : t('customer_manager_360.action.show_sku_for', { sku: sku.sku_name })
                }
                title={openBlockReasonKey === null ? undefined : t(openBlockReasonKey)}
                aria-describedby={showBlocked ? showReasonId : undefined}
                onClick={() => {
                  if (visible) {
                    void onToggleVisibility(row);
                  } else if (confirmShow) {
                    setConfirmShow(false);
                    void onToggleVisibility(row);
                  } else {
                    setConfirmShow(true);
                  }
                }}
                data-testid={`customer-sku-visibility-${sku.sku_id}`}
              >
                {visible ? (
                  <EyeOff aria-hidden="true" className="h-3.5 w-3.5" />
                ) : (
                  <Eye aria-hidden="true" className="h-3.5 w-3.5" />
                )}
                {visible
                  ? t('customer_manager_360.action.hide_sku')
                  : confirmShow
                    ? t('customer_manager_360.action.confirm_show_sku')
                    : t('customer_manager_360.action.show_sku')}
              </Button>
              </PlanLockWrap>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {activationRateMissing && !pendingDiscount ? (
              <StatusChip
                variant="warning"
                label={t('customer_manager_360.visibility.pricing_required')}
                testId={`customer-sku-pricing-required-${sku.sku_id}`}
              />
            ) : null}
            {hasAppliedRate ? (
              <StatusChip
                variant="ok"
                label={t('customer_manager_360.visibility.rate_applied')}
                testId={`customer-sku-rate-applied-${sku.sku_id}`}
              />
            ) : null}
            {pendingDiscount ? (
              <StatusChip
                variant="warning"
                label={t('customer_manager_360.sku_rates.pending_discount')}
                testId={`customer-sku-pending-discount-${sku.sku_id}`}
              />
            ) : null}
          </div>
          {canWrite && showBlocked ? (
            <span
              id={showReasonId}
              data-testid={`customer-sku-open-block-reason-${sku.sku_id}`}
              className="max-w-64 text-label text-warn"
            >
              {t(
                pendingDiscount
                  ? 'customer_manager_360.sku_rates.open_blocked_pending_discount'
                  : 'customer_manager_360.sku_rates.open_blocked_unpriced',
              )}
            </span>
          ) : null}
          {convergenceState?.refreshBlocked ? (
            <div className="flex flex-col items-start gap-1.5">
              <span
                role="alert"
                className="text-label text-bad-bright"
                data-testid={`customer-sku-convergence-error-${sku.sku_id}`}
              >
                {convergenceState.kind === 'unknown'
                  ? t('customer_manager_360.sku_rates.convergence_unknown_hint')
                  : t('customer_manager_360.sku_rates.convergence_refresh_failed')}
              </span>
              <Button
                size="sm"
                variant="ghost"
                disabled={convergenceState.refreshing}
                onClick={() => void onRetryConvergence(sku.sku_id)}
                data-testid={`customer-sku-convergence-retry-${sku.sku_id}`}
              >
                <RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />
                {t('customer_manager_360.sku_rates.retry_convergence')}
              </Button>
            </div>
          ) : null}
        </div>
      </Td>
      <Td num>{fmtRate(sku.platform_rate_multiplier)}</Td>
      <Td num>
        <div className="flex flex-col items-end gap-1">
          <span>
            {row.floor === null
              ? t('customer_manager_360.rate.floor_unset')
              : fmtRate(row.floor)}
          </span>
          {row.upstreamCostCnyPerUsd !== null ? (
            <span className="font-sans text-micro text-paper-low">
              {t('customer_manager_360.rate.cny_per_credit', {
                amount: formatCnyCents(Math.round(row.upstreamCostCnyPerUsd * 100), false),
              })}
            </span>
          ) : null}
        </div>
      </Td>
      <Td num>{fmtRate(sku.rate_multiplier)}</Td>
      <Td>
        {canWrite ? (
          <PlanLockWrap locked={lockedByPlan} hint={lockHint}>
            <Input
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
              aria-label={t('customer_manager_360.column.note')}
              className="min-w-40"
              disabled={!editorsEnabled}
            />
          </PlanLockWrap>
        ) : (
          <span className="text-dense text-paper-low">{sku.note ?? '—'}</span>
        )}
      </Td>
      <Td>
        <div className="flex min-w-64 flex-col gap-2">
          {canWrite ? (
            <PlanLockWrap locked={lockedByPlan} hint={lockHint}>
            <div className="flex gap-2">
              <Input
                mono
                value={value}
                onChange={(e) => setValue(e.currentTarget.value)}
                type="number"
                step="0.0001"
                // Sales view (#1106): below-floor values are enterable — the
                // save converts into a pending approval instead of applying.
                min={salesView ? undefined : row.floor ?? undefined}
                aria-label={t('customer_manager_360.column.effective_rate')}
                className="min-w-0 flex-1"
                disabled={!editorsEnabled}
              />
              <Button
                disabled={isPending || !editorsEnabled}
                aria-label={t('customer_manager_360.action.save_rate_for', {
                  sku: sku.sku_name,
                })}
                onClick={() => void onSave(row, parsedRate(), note.trim() || null)}
                data-testid={`customer-sku-rate-save-${sku.sku_id}`}
              >
                <Save aria-hidden="true" className="h-3.5 w-3.5" />
                {t('common.save')}
              </Button>
              <Button
                variant="ghost"
                disabled={isPending || clearBlocked || !editorsEnabled}
                aria-label={t('customer_manager_360.action.inherit_rate_for', {
                  sku: sku.sku_name,
                })}
                aria-describedby={clearBlocked ? clearReasonId : undefined}
                onClick={() => void onSave(row, null, note.trim() || null)}
                data-testid={`customer-sku-rate-inherit-${sku.sku_id}`}
              >
                <Undo2 aria-hidden="true" className="h-3.5 w-3.5" />
                {t('customer_manager_360.action.inherit')}
              </Button>
            </div>
            </PlanLockWrap>
          ) : null}
          {/*
            Saving here creates a per-model exception out of an inherited rate.
            The editor stays live — that is how an exception is legitimately
            made — but the consequence is stated, because the numbers on screen
            are identical before and after.
          */}
          {canWrite && inheritsLineRate ? (
            <span
              className="text-label text-paper-low"
              data-testid={`customer-sku-exception-hint-${sku.sku_id}`}
            >
              {t('customer_manager_360.rate_source.exception_hint')}
            </span>
          ) : null}
          {canWrite && clearBlocked ? (
            <span id={clearReasonId} className="text-label text-warn">
              {t(
                ownsSkuRate
                  ? 'customer_manager_360.visibility.clear_requires_hidden'
                  : 'customer_manager_360.rate_source.inherit_blocked',
              )}
            </span>
          ) : null}
          {canWrite && belowSalesFloor && !pendingDiscount ? (
            <span
              className="text-label text-warn"
              data-testid={`customer-sku-below-floor-hint-${sku.sku_id}`}
            >
              {t('customer_manager_360.sku_rates.below_floor_hint', {
                floor: fmtRate(row.floor),
              })}
            </span>
          ) : null}
          {error !== null ? (
            <span role="alert" className="text-label text-bad">{error}</span>
          ) : null}
        </div>
      </Td>
    </Tr>
  );
}

function LifecycleRow({ row }: { readonly row: CustomerManagerLifecycleEvidence }): JSX.Element {
  return (
    <div className="grid gap-2 rounded-lg bg-well px-3 py-3 text-dense md:grid-cols-[1.3fr_0.5fr_0.8fr_0.6fr]">
      <span className="font-mono text-paper">{truncate(row.request_id)}</span>
      <StatusChip variant={row.status_code >= 400 ? 'error' : 'ok'} label={row.status_code} />
      <span className="text-paper-mid">{row.lifecycle_status ?? '—'}</span>
      <span className="font-mono text-paper-low">{formatLocalTimestamp(row.created_at)}</span>
    </div>
  );
}

/**
 * #1958: `amountCnyCents === null` means "we could not convert this amount"
 * (no display rate). That renders as an explicit missing state — never as a
 * zero-length bar, which reads as "this customer owes nothing".
 */
function MoneyBar({
  testId,
  label,
  value,
  amountCnyCents,
  maxCnyCents,
}: {
  readonly testId: string;
  readonly label: string;
  readonly value: string;
  readonly amountCnyCents: number | null;
  readonly maxCnyCents: number;
}): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-3 rounded-lg bg-well px-3 py-3" data-testid={testId}>
      <div className="flex flex-col gap-1">
        <span className="text-label text-paper-low">{label}</span>
        <span className="font-mono text-stat text-paper">{value}</span>
      </div>
      {amountCnyCents === null ? (
        <p className="m-0 text-micro text-paper-low" data-testid={`${testId}-unavailable`}>
          {t('customer_manager_360.metric.rate_unavailable')}
        </p>
      ) : (
        <div className="h-2 overflow-hidden rounded-full bg-canvas">
          <div className="h-full rounded-full bg-signal" style={{ width: percentOf(amountCnyCents, maxCnyCents) }} />
        </div>
      )}
    </div>
  );
}

function TextField({
  fieldId,
  label,
  value,
  onChange,
  type = 'text',
}: {
  readonly fieldId: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly type?: 'text' | 'number';
}): JSX.Element {
  return (
    <FormField label={label} fieldId={fieldId}>
      <Input
        id={fieldId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    </FormField>
  );
}

function KeyValue({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-1 rounded-lg bg-well px-3 py-2">
      <span className="text-label text-paper-low">{label}</span>
      <span className="break-words text-body text-paper-mid">{value}</span>
    </div>
  );
}

function LoadingPanel(): JSX.Element {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line-soft bg-card p-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-12 animate-pulse rounded-lg bg-raised" aria-hidden="true" />
      ))}
    </div>
  );
}

function ErrorBanner({
  testId,
  title,
  error,
  onRetry,
}: {
  readonly testId: string;
  readonly title: string;
  readonly error: Error;
  readonly onRetry: () => void;
}): JSX.Element {
  const t = useT();
  return (
    <div
      role="alert"
      data-testid={testId}
      className="flex items-center justify-between gap-4 rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3"
    >
      <div className="flex flex-col gap-1">
        <span className="text-body font-medium text-bad-bright">{title}</span>
        <span className="text-dense text-bad">
          {error instanceof OpsApiError
            ? `${error.code}: ${error.message}`
            : t('common.network_error')}
        </span>
      </div>
      <Button variant="danger" size="sm" onClick={onRetry}>
        {t('common.retry')}
      </Button>
    </div>
  );
}
